import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Users, DollarSign, CreditCard, AlertOctagon, Shield, Handshake,
  Inbox, LifeBuoy, FileText, BarChart3, Settings, LogOut, Search, Download, Loader2,
  Menu, X, HardDrive, RefreshCw, Trash2, Ban, Unlock, ChevronRight, ShieldCheck, Plus,
  Heart, Wallet, Receipt, Check, CheckCircle2, XCircle, Mail, Tag, Copy, Play, Pause, Pencil, Building2, Image as ImageIcon,
  BadgeCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer,
} from 'recharts';
import { adminAxios, clearAdminToken, getAdminToken, C, fmtMoney, fmtDate, fmtDateTime, apiError } from './adminApi';

const MENU = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, cap: 'view_overview' },
  { id: 'subscribers', label: 'Subscribers', icon: Users, cap: 'view_users' },
  { id: 'revenue', label: 'Revenue', icon: DollarSign, cap: 'view_revenue' },
  { id: 'ambassadors', label: 'Ambassadors', icon: Heart, cap: 'view_overview' },
  { id: 'commissions', label: 'Commissions', icon: Receipt, cap: 'view_revenue' },
  { id: 'payouts', label: 'Payouts', icon: Wallet, cap: 'view_revenue' },
  { id: 'discounts', label: 'Discount Codes', icon: Tag, cap: 'view_discounts' },
  { id: 'groups', label: 'Groups', icon: Building2, cap: 'view_discounts' },
  { id: 'savings-release', label: 'Savings Release', icon: ShieldCheck, cap: 'view_overview' },
  { id: 'verification', label: 'Verification', icon: BadgeCheck, cap: 'view_overview' },
  { id: 'materials', label: 'Marketing Materials', icon: ImageIcon, cap: 'view_overview' },
  { id: 'payments', label: 'Payments', icon: CreditCard, cap: 'view_payments' },
  { id: 'errors', label: 'Errors', icon: AlertOctagon, cap: 'view_overview' },
  { id: 'security', label: 'Security', icon: Shield, cap: 'view_overview' },
  { id: 'content-monitoring', label: 'Content', icon: HardDrive, cap: 'view_users' },
  { id: 'partners', label: 'Partners', icon: Handshake, cap: 'view_partners' },
  { id: 'leads', label: 'Leads', icon: Inbox, cap: 'view_leads' },
  { id: 'support', label: 'Support', icon: LifeBuoy, cap: 'view_support' },
  { id: 'site-content', label: 'Site Content', icon: FileText, cap: 'view_content' },
  { id: 'email-templates', label: 'Email Delivery', icon: Mail, cap: 'view_content' },
  { id: 'reports', label: 'Reports', icon: BarChart3, cap: 'view_reports' },
  { id: 'settings', label: 'Settings', icon: Settings, cap: 'view_overview' },
];

const allowed = (me, cap) => me?.caps?.includes('*') || me?.caps?.includes(cap);

export default function AdminApp() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [checking, setChecking] = useState(true);
  const [section, setSection] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (!getAdminToken()) { navigate('/admin/login'); return; }
    adminAxios().get('/me')
      .then((r) => setMe(r.data))
      .catch(() => { clearAdminToken(); navigate('/admin/login'); })
      .finally(() => setChecking(false));
  }, [navigate]);

  const logout = async () => {
    try { await adminAxios().post('/logout'); } catch { /* ignore */ }
    clearAdminToken(); navigate('/admin/login');
  };

  if (checking) return <div className="min-h-screen flex items-center justify-center bg-[#F7F8FA]"><Loader2 className="h-8 w-8 animate-spin" style={{ color: C.navy }} /></div>;

  const visibleMenu = MENU.filter((m) => allowed(me, m.cap));
  const active = MENU.find((m) => m.id === section);

  return (
    <div className="min-h-screen bg-[#F7F8FA] flex overflow-x-hidden" data-testid="admin-app">
      {/* Sidebar */}
      <aside className={`fixed lg:static z-40 w-64 h-screen flex-col shrink-0 transition-transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 flex`} style={{ background: C.navy }}>
        <div className="h-16 flex items-center gap-2.5 px-5 border-b border-white/10">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: C.gold }}><ShieldCheck className="h-5 w-5 text-white" /></div>
          <div><p className="text-white font-bold leading-tight">EasyPassing</p><p className="text-white/50 text-xs">Admin Console</p></div>
        </div>
        <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-0.5">
          {visibleMenu.map((m) => (
            <button key={m.id} data-testid={`admin-nav-${m.id}`} onClick={() => { setSection(m.id); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${section === m.id ? 'text-white' : 'text-white/60 hover:text-white hover:bg-white/5'}`}
              style={section === m.id ? { background: C.navy2 } : {}}>
              <m.icon className="h-[18px] w-[18px]" /> {m.label}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10">
          <div className="px-3 py-2 mb-1">
            <p className="text-white text-sm font-semibold truncate">{me?.name}</p>
            <p className="text-white/50 text-xs capitalize">{me?.role}</p>
          </div>
          <button data-testid="admin-logout" onClick={logout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-white/70 hover:text-white hover:bg-white/5">
            <LogOut className="h-[18px] w-[18px]" /> Logout
          </button>
        </div>
      </aside>
      {sidebarOpen && <div className="fixed inset-0 bg-black/30 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 bg-white border-b border-[#E8EAEE] flex items-center justify-between px-4 sm:px-6 sticky top-0 z-20">
          <div className="flex items-center gap-3">
            <button className="lg:hidden" onClick={() => setSidebarOpen(true)}><Menu className="h-6 w-6" style={{ color: C.navy }} /></button>
            <h1 className="text-lg font-bold" style={{ color: C.ink }}>{active?.label}</h1>
          </div>
          <span className="text-xs px-3 py-1.5 rounded-full font-medium capitalize" style={{ background: C.cream, color: C.navy }}>{me?.role} access</span>
        </header>
        <main className="flex-1 p-4 sm:p-6 overflow-x-hidden">
          <Section id={section} me={me} />
        </main>
      </div>
    </div>
  );
}

// ---------- shared UI ----------
const Card = ({ children, className = '' }) => <div className={`bg-white rounded-2xl border border-[#E8EAEE] ${className}`}>{children}</div>;
const Loading = () => <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin" style={{ color: C.navy }} /></div>;
const Empty = ({ text }) => <div className="text-center py-16 text-[#8D8880] text-sm" data-testid="admin-empty">{text}</div>;

const Stat = ({ label, value, accent }) => (
  <Card className="p-5">
    <p className="text-xs font-medium text-[#8D8880] uppercase tracking-wide">{label}</p>
    <p className="text-2xl font-bold mt-1.5" style={{ color: accent || C.ink }}>{value}</p>
  </Card>
);

function useData(path, deps = []) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const reload = useCallback(() => {
    setLoading(true);
    adminAxios().get(path).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  }, [path]); // eslint-disable-line
  useEffect(() => { reload(); }, deps); // eslint-disable-line
  return { data, loading, reload };
}

const badge = (text, color) => <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: `${color}22`, color }}>{text}</span>;
const statusColor = (s) => ({ active: C.green, paid: C.green, free: C.mute, suspended: C.red, failed: C.red, new: C.navy2, open: C.navy2, fixed: C.green, contacted: C.green, delivered: C.green }[s] || C.mute);

// ---------- section router ----------
function Section({ id, me }) {
  switch (id) {
    case 'dashboard': return <Overview />;
    case 'subscribers': return <Subscribers me={me} />;
    case 'revenue': return <Revenue />;
    case 'ambassadors': return <Ambassadors me={me} />;
    case 'commissions': return <Commissions me={me} />;
    case 'payouts': return <Payouts_ me={me} />;
    case 'discounts': return <Discounts me={me} />;
    case 'groups': return <Groups me={me} />;
    case 'savings-release': return <SavingsRelease me={me} />;
    case 'verification': return <VerificationStatus />;
    case 'materials': return <Materials me={me} />;
    case 'payments': return <Payments />;
    case 'errors': return <Errors me={me} />;
    case 'security': return <Security me={me} />;
    case 'content-monitoring': return <ContentMonitoring />;
    case 'partners': return <Partners me={me} />;
    case 'leads': return <Leads me={me} />;
    case 'support': return <Support me={me} />;
    case 'site-content': return <SiteContent me={me} />;
    case 'email-templates': return <EmailTemplates me={me} />;
    case 'reports': return <Reports />;
    case 'settings': return <SettingsSection me={me} />;
    default: return null;
  }
}

// ---------- Verification status widget ----------
const ID_COLORS = { verified: '#7FA98C', processing: '#2B4C7E', requires_input: '#C9A24B', canceled: '#C0563F', not_started: '#8D8880', pending: '#C9A24B' };
const CONNECT_COLORS = { payouts_enabled: '#7FA98C', pending_review: '#C9A24B', onboarding_incomplete: '#C0563F' };
function VerificationStatus() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const load = useCallback((refresh = false) => {
    if (refresh) setRefreshing(true); else setLoading(true);
    adminAxios().get(`/verification-status${refresh ? '?refresh=true' : ''}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => { setLoading(false); setRefreshing(false); });
  }, []);
  useEffect(() => { load(false); }, [load]);
  if (loading) return <Loading />;
  const id = data?.identity || { executors: [], stats: {} };
  const cn = data?.connect || { ambassadors: [], stats: {} };
  return (
    <div className="space-y-6" data-testid="admin-verification-status">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-bold" style={{ color: C.ink }}>Verification Status</h2>
          <p className="text-sm" style={{ color: C.mute }}>Executor Stripe Identity checks and ambassador Stripe Connect payout readiness at a glance.</p>
        </div>
        <button onClick={() => load(true)} disabled={refreshing} className="text-xs font-semibold px-4 py-2 rounded-xl text-white flex items-center gap-1.5 disabled:opacity-50" style={{ background: C.navy }} data-testid="verification-refresh">
          <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} /> Sync live from Stripe
        </button>
      </div>

      {/* Identity */}
      <div className="space-y-3">
        <h3 className="text-sm font-bold uppercase tracking-wide" style={{ color: C.navy }}>Executor ID Verification</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Total" value={id.stats.total ?? 0} />
          <Stat label="Verified" value={id.stats.verified ?? 0} accent={ID_COLORS.verified} />
          <Stat label="Processing" value={id.stats.processing ?? 0} accent={ID_COLORS.processing} />
          <Stat label="Needs input" value={id.stats.requires_input ?? 0} accent={ID_COLORS.requires_input} />
        </div>
        <Card className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[#8D8880] border-b border-[#F1F2F4]">
              <th className="p-3 whitespace-nowrap">Executor</th><th className="p-3 whitespace-nowrap">Account holder</th><th className="p-3 whitespace-nowrap">Identity status</th><th className="p-3 whitespace-nowrap">Verified</th><th className="p-3 whitespace-nowrap">Updated</th>
            </tr></thead>
            <tbody>
              {id.executors.map((e) => (
                <tr key={e.release_request_id} className="border-b border-[#F1F2F4]" data-testid={`verification-executor-${e.release_request_id}`}>
                  <td className="p-3 whitespace-nowrap">{e.executor_name || '—'}<div className="text-xs text-[#8D8880]">{e.executor_email}</div></td>
                  <td className="p-3 whitespace-nowrap">{e.owner_name || '—'}</td>
                  <td className="p-3 whitespace-nowrap">{badge((e.identity_status || '').replace(/_/g, ' '), ID_COLORS[e.identity_status] || C.mute)}</td>
                  <td className="p-3 whitespace-nowrap">{e.identity_verified ? <CheckCircle2 className="h-4 w-4" style={{ color: ID_COLORS.verified }} /> : <XCircle className="h-4 w-4" style={{ color: C.mute }} />}</td>
                  <td className="p-3 whitespace-nowrap text-xs text-[#8D8880]">{e.updated_at ? fmtDate(e.updated_at) : '—'}</td>
                </tr>
              ))}
              {id.executors.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-[#8D8880]">No executor verifications started yet.</td></tr>}
            </tbody>
          </table>
        </Card>
      </div>

      {/* Connect */}
      <div className="space-y-3">
        <h3 className="text-sm font-bold uppercase tracking-wide" style={{ color: C.navy }}>Ambassador Payout Onboarding</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Onboarded" value={cn.stats.total ?? 0} />
          <Stat label="Payouts enabled" value={cn.stats.payouts_enabled ?? 0} accent={CONNECT_COLORS.payouts_enabled} />
          <Stat label="Pending review" value={cn.stats.pending_review ?? 0} accent={CONNECT_COLORS.pending_review} />
          <Stat label="Incomplete" value={cn.stats.onboarding_incomplete ?? 0} accent={CONNECT_COLORS.onboarding_incomplete} />
        </div>
        <Card className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[#8D8880] border-b border-[#F1F2F4]">
              <th className="p-3 whitespace-nowrap">Ambassador</th><th className="p-3 whitespace-nowrap">Connect state</th><th className="p-3 whitespace-nowrap">Details submitted</th><th className="p-3 whitespace-nowrap">Payouts</th><th className="p-3 whitespace-nowrap">Requirements due</th><th className="p-3 whitespace-nowrap">Synced</th>
            </tr></thead>
            <tbody>
              {cn.ambassadors.map((a) => (
                <tr key={a.ambassador_id} className="border-b border-[#F1F2F4]" data-testid={`verification-ambassador-${a.ambassador_id}`}>
                  <td className="p-3 whitespace-nowrap">{a.name || '—'}<div className="text-xs text-[#8D8880]">{a.email}</div></td>
                  <td className="p-3 whitespace-nowrap">{badge((a.connect_state || '').replace(/_/g, ' '), CONNECT_COLORS[a.connect_state] || C.mute)}</td>
                  <td className="p-3 whitespace-nowrap">{a.details_submitted ? <CheckCircle2 className="h-4 w-4" style={{ color: CONNECT_COLORS.payouts_enabled }} /> : <XCircle className="h-4 w-4" style={{ color: C.mute }} />}</td>
                  <td className="p-3 whitespace-nowrap">{a.payouts_enabled ? <CheckCircle2 className="h-4 w-4" style={{ color: CONNECT_COLORS.payouts_enabled }} /> : <XCircle className="h-4 w-4" style={{ color: C.mute }} />}</td>
                  <td className="p-3 text-xs" style={{ color: (a.requirements_due || []).length ? C.red : C.mute }}>{(a.requirements_due || []).length ? a.requirements_due.join(', ') : '—'}</td>
                  <td className="p-3 whitespace-nowrap text-xs text-[#8D8880]">{a.synced_at ? fmtDate(a.synced_at) : 'Not synced'}</td>
                </tr>
              ))}
              {cn.ambassadors.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-[#8D8880]">No ambassadors have started Stripe onboarding yet.</td></tr>}
            </tbody>
          </table>
        </Card>
      </div>
    </div>
  );
}

// ---------- Savings Release oversight ----------
const REL_COLORS = { released: '#7FA98C', in_progress: '#2B4C7E', paused: '#C9A24B', rejected: '#C0563F' };
function SavingsRelease({ me }) {
  const { data, loading, reload } = useData('/savings-release');
  const [detail, setDetail] = useState(null);
  const canManage = me?.role === 'owner';
  const openDetail = async (id) => {
    try { const r = await adminAxios().get(`/savings-release/${id}`); setDetail(r.data); }
    catch (e) { toast.error(apiError(e)); }
  };
  const act = async (id, action) => {
    const reason = action === 'reject' || action === 'pause' ? window.prompt(`Reason for ${action}?`) : null;
    try { await adminAxios().post(`/savings-release/${id}/action`, { action, reason }); toast.success(`Release ${action}d`); setDetail(null); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };
  if (loading) return <Loading />;
  const s = data?.stats || {};
  return (
    <div className="space-y-4" data-testid="admin-savings-release">
      <div>
        <h2 className="text-xl font-bold" style={{ color: C.ink }}>Savings Release</h2>
        <p className="text-sm" style={{ color: C.mute }}>Oversee executor release requests. EasyPassing sends information only — it never transfers funds.</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Total requests" value={s.total ?? 0} />
        <Stat label="Released" value={s.released ?? 0} accent={REL_COLORS.released} />
        <Stat label="In progress" value={s.in_progress ?? 0} accent={REL_COLORS.in_progress} />
        <Stat label="Paused" value={s.paused ?? 0} accent={REL_COLORS.paused} />
      </div>
      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[#8D8880] border-b border-[#F1F2F4]">
            <th className="p-3 whitespace-nowrap">Account holder</th><th className="p-3 whitespace-nowrap">Executor</th><th className="p-3 whitespace-nowrap">Status</th><th className="p-3 whitespace-nowrap">Notifications</th><th className="p-3 whitespace-nowrap">Aftercare</th><th className="p-3 whitespace-nowrap">Updated</th><th className="p-3"></th>
          </tr></thead>
          <tbody>
            {(data?.requests || []).map((r) => (
              <tr key={r.release_request_id} className="border-b border-[#F1F2F4]" data-testid={`release-row-${r.release_request_id}`}>
                <td className="p-3 whitespace-nowrap">{r.owner?.name || '—'}<div className="text-xs text-[#8D8880]">{r.owner?.plan}</div></td>
                <td className="p-3 whitespace-nowrap">{r.executor?.full_name || '—'}<div className="text-xs text-[#8D8880]">{r.executor?.email}</div></td>
                <td className="p-3 whitespace-nowrap">{badge((r.release_status || '').replace('_', ' '), REL_COLORS[r.release_status] || C.mute)}</td>
                <td className="p-3 whitespace-nowrap">{r.notifications}</td>
                <td className="p-3 whitespace-nowrap">{r.aftercare?.status || '—'}</td>
                <td className="p-3 whitespace-nowrap text-xs text-[#8D8880]">{fmtDate(r.updated_at)}</td>
                <td className="p-3 whitespace-nowrap"><button onClick={() => openDetail(r.release_request_id)} className="text-xs font-semibold px-3 py-1.5 rounded-full text-white" style={{ background: C.navy }} data-testid={`release-view-${r.release_request_id}`}>Review</button></td>
              </tr>
            ))}
            {(data?.requests || []).length === 0 && <tr><td colSpan={7} className="p-8 text-center text-[#8D8880]">No release requests yet.</td></tr>}
          </tbody>
        </table>
      </Card>
      {detail && (
        <Modal title="Release request" onClose={() => setDetail(null)}>
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-2 gap-2">
              <Field label="Status" value={(detail.request.release_status || '').replace('_', ' ')} />
              <Field label="Date of passing" value={detail.request.death_date || '—'} />
              <Field label="Signed by" value={detail.request.signed_legal_name || '—'} />
              <Field label="Biometric" value={detail.request.biometric_verified ? 'Verified' : 'Not verified'} />
              <Field label="ID verification" value={detail.request.identity_verification_status || 'not started'} />
              <Field label="Released at" value={detail.request.released_at ? fmtDateTime(detail.request.released_at) : '—'} />
            </div>
            <div>
              <p className="font-semibold mb-1" style={{ color: C.ink }}>Documents on file ({detail.documents.length})</p>
              <div className="flex flex-wrap gap-1.5">{detail.documents.map((d) => <span key={d.field}>{badge(d.field.replace('_', ' '), C.navy2)}</span>)}{detail.documents.length === 0 && <span className="text-[#8D8880]">None</span>}</div>
            </div>
            <div>
              <p className="font-semibold mb-1" style={{ color: C.ink }}>Beneficiary notifications ({detail.notifications.length})</p>
              {detail.notifications.map((n, i) => <div key={i} className="text-xs flex justify-between border-b border-[#F1F2F4] py-1"><span>{n.email}</span>{badge(n.status, n.status === 'sent' ? C.green : C.red)}</div>)}
              {detail.notifications.length === 0 && <span className="text-xs text-[#8D8880]">None</span>}
            </div>
            <div>
              <p className="font-semibold mb-1" style={{ color: C.ink }}>Audit log</p>
              <div className="max-h-40 overflow-y-auto space-y-1">{detail.audit.map((a, i) => <div key={i} className="text-xs text-[#57534E] border-b border-[#F1F2F4] py-1">{fmtDateTime(a.created_at)} — {a.action_description}</div>)}{detail.audit.length === 0 && <span className="text-xs text-[#8D8880]">No entries</span>}</div>
            </div>
            {canManage && detail.request.release_status !== 'released' && (
              <div className="flex flex-wrap gap-2 pt-1">
                {detail.request.release_status !== 'paused' && <button onClick={() => act(detail.request.release_request_id, 'pause')} className="text-xs font-semibold px-4 py-2 rounded-xl" style={{ background: '#FBF0D8', color: '#8a6d1f' }} data-testid="release-pause">Pause for review</button>}
                {detail.request.release_status === 'paused' && <button onClick={() => act(detail.request.release_request_id, 'resume')} className="text-xs font-semibold px-4 py-2 rounded-xl" style={{ background: '#EBF0F5', color: C.navy }} data-testid="release-resume">Resume</button>}
                <button onClick={() => act(detail.request.release_request_id, 'reject')} className="text-xs font-semibold px-4 py-2 rounded-xl" style={{ background: '#FCECE8', color: C.red }} data-testid="release-reject">Reject</button>
              </div>
            )}
            {!canManage && <p className="text-xs text-[#8D8880]">Only the Owner role can pause or reject a release.</p>}
          </div>
        </Modal>
      )}
    </div>
  );
}
const Field = ({ label, value }) => <div><p className="text-xs font-semibold uppercase text-[#8D8880]">{label}</p><p className="capitalize" style={{ color: C.ink }}>{value}</p></div>;


// ---------- Overview ----------
function Overview() {
  const { data, loading } = useData('/overview');
  if (loading) return <Loading />;
  const d = data || {};
  const cards = [
    ['Total Users', d.total_users, C.navy], ['Active Subscribers', d.active_subscribers, C.green],
    ['Free Users', d.free_users, C.mute], ['MRR', fmtMoney(d.mrr), C.gold],
    ['Total Revenue', fmtMoney(d.total_revenue), C.green], ['New Signups (mo)', d.new_signups_month, C.navy2],
    ['Cancelled', d.cancelled_subscriptions, C.red], ['Failed Payments', d.failed_payments, C.red],
    ['App Errors', d.errors, C.red], ['Security Alerts', d.security_alerts, C.gold],
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4" data-testid="admin-overview">
      {cards.map(([l, v, a]) => <Stat key={l} label={l} value={v ?? 0} accent={a} />)}
    </div>
  );
}

// ---------- Subscribers ----------
function Subscribers({ me }) {
  const [search, setSearch] = useState('');
  const [plan, setPlan] = useState('all');
  const [status, setStatus] = useState('all');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const canEdit = allowed(me, 'edit_users');
  const canDelete = me?.caps?.includes('*');

  const load = useCallback(() => {
    setLoading(true);
    adminAxios().get('/users', { params: { search, plan, status, limit: 200 } })
      .then((r) => setRows(r.data.users)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  }, [search, plan, status]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const act = async (uid, path, method = 'post', label) => {
    try { await adminAxios()[method](`/users/${uid}/${path}`); toast.success(label); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const del = async (uid) => {
    if (!window.confirm('Permanently delete this user and all their data? This cannot be undone.')) return;
    try { await adminAxios().delete(`/users/${uid}`); toast.success('User deleted'); load(); setDetail(null); }
    catch (e) { toast.error(apiError(e)); }
  };
  const changePlan = async (uid, newPlan) => {
    try { await adminAxios().post(`/users/${uid}/plan`, { plan: newPlan }); toast.success(`Plan → ${newPlan}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9C948B]" />
          <input data-testid="admin-user-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name or email…"
            className="w-full h-10 pl-9 pr-3 rounded-xl border border-[#E3E1DC] outline-none text-sm" />
        </div>
        <select data-testid="admin-filter-plan" value={plan} onChange={(e) => setPlan(e.target.value)} className="h-10 px-3 rounded-xl border border-[#E3E1DC] text-sm">
          <option value="all">All plans</option><option value="free">Free</option><option value="premium">Premium</option><option value="family">Family</option>
        </select>
        <select data-testid="admin-filter-status" value={status} onChange={(e) => setStatus(e.target.value)} className="h-10 px-3 rounded-xl border border-[#E3E1DC] text-sm">
          <option value="all">All status</option><option value="active">Active (paid)</option><option value="free">Free</option><option value="suspended">Suspended</option>
        </select>
        <button data-testid="admin-export-users" onClick={() => downloadWithAuth('/users/export/csv', 'subscribers.csv')} className="h-10 px-4 rounded-xl text-white text-sm font-medium flex items-center gap-2" style={{ background: C.navy }}>
          <Download className="h-4 w-4" /> CSV
        </button>
      </div>

      {loading ? <Loading /> : rows.length === 0 ? <Empty text="No users match your filters." /> : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[#8D8880] border-b border-[#E8EAEE]">
                {['User', 'Plan', 'Status', 'Signup', 'Last Login', 'Monthly', 'Contacts', 'Uploads', ''].map((h) => <th key={h} className="px-4 py-3 font-medium whitespace-nowrap">{h}</th>)}
              </tr></thead>
              <tbody>
                {rows.map((u) => (
                  <tr key={u.user_id} data-testid={`admin-user-row-${u.user_id}`} className="border-b border-[#F1F2F4] hover:bg-[#FAFBFC]">
                    <td className="px-4 py-3"><p className="font-medium" style={{ color: C.ink }}>{u.name}</p><p className="text-xs text-[#8D8880]">{u.email}</p></td>
                    <td className="px-4 py-3 capitalize">{u.plan}</td>
                    <td className="px-4 py-3">{badge(u.subscription_status, statusColor(u.subscription_status))}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-[#57534E]">{fmtDate(u.signup_date)}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-[#57534E]">{fmtDate(u.last_login)}</td>
                    <td className="px-4 py-3">{fmtMoney(u.monthly_amount)}</td>
                    <td className="px-4 py-3">{u.trusted_contacts}</td>
                    <td className="px-4 py-3">{u.storage_used}</td>
                    <td className="px-4 py-3"><button onClick={() => setDetail(u)} className="text-xs font-semibold" style={{ color: C.navy2 }}>View</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {detail && (
        <Modal title={detail.name} onClose={() => setDetail(null)}>
          <div className="space-y-1 text-sm text-[#57534E]">
            <p><b>Email:</b> {detail.email}</p>
            <p><b>Plan:</b> <span className="capitalize">{detail.plan}</span> · {fmtMoney(detail.monthly_amount)}/mo</p>
            <p><b>Status:</b> {detail.subscription_status}</p>
            <p><b>Signed up:</b> {fmtDate(detail.signup_date)} · <b>Last login:</b> {fmtDate(detail.last_login)}</p>
            <p><b>Trusted contacts:</b> {detail.trusted_contacts} · <b>Uploads:</b> {detail.storage_used}</p>
          </div>
          {canEdit && (
            <div className="mt-4 space-y-3">
              <div className="flex items-center gap-2">
                <span className="text-sm text-[#57534E]">Change plan:</span>
                {['free', 'premium', 'family'].map((p) => (
                  <button key={p} onClick={() => changePlan(detail.user_id, p)} disabled={detail.plan === p}
                    className="text-xs px-3 py-1.5 rounded-full border capitalize disabled:opacity-40" style={{ borderColor: C.line, color: C.navy }}>{p}</button>
                ))}
              </div>
              <div className="flex flex-wrap gap-2">
                {detail.account_status === 'suspended'
                  ? <ActionBtn onClick={() => act(detail.user_id, 'reactivate', 'post', 'Reactivated')} icon={Unlock}>Reactivate</ActionBtn>
                  : <ActionBtn onClick={() => act(detail.user_id, 'suspend', 'post', 'Suspended')} icon={Ban} color={C.red}>Suspend</ActionBtn>}
                <ActionBtn onClick={() => act(detail.user_id, 'reset-password', 'post', 'Password reset link generated')} icon={RefreshCw}>Force password reset</ActionBtn>
                {canDelete && <ActionBtn onClick={() => del(detail.user_id)} icon={Trash2} color={C.red}>Delete account</ActionBtn>}
              </div>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}

async function downloadWithAuth(path, filename) {
  try {
    const r = await adminAxios().get(path, { responseType: 'blob' });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  } catch (e) { toast.error(apiError(e)); }
}

const ActionBtn = ({ onClick, icon: Icon, children, color }) => (
  <button onClick={onClick} className="text-xs px-3 py-2 rounded-xl border font-medium flex items-center gap-1.5" style={{ borderColor: C.line, color: color || C.navy }}>
    <Icon className="h-3.5 w-3.5" /> {children}
  </button>
);

// ---------- Revenue ----------
function Revenue() {
  const { data, loading } = useData('/revenue');
  if (loading) return <Loading />;
  const d = data || {};
  const pie = [
    { name: 'Free', value: d.free_vs_paid?.free || 0, color: C.mute },
    { name: 'Paid', value: d.free_vs_paid?.paid || 0, color: C.green },
  ];
  const planPie = [
    { name: 'Premium', value: d.plan_breakdown?.premium || 0, color: C.navy2 },
    { name: 'Family', value: d.plan_breakdown?.family || 0, color: C.gold },
    { name: 'Free', value: d.plan_breakdown?.free || 0, color: C.mute },
  ];
  return (
    <div className="space-y-4" data-testid="admin-revenue">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Monthly Income" value={fmtMoney(d.monthly_income)} accent={C.gold} />
        <Stat label="Annual Income" value={fmtMoney(d.annual_income)} accent={C.green} />
        <Stat label="Conversion" value={`${d.conversion_rate}%`} accent={C.navy} />
        <Stat label="Churn" value={`${d.churn_rate}%`} accent={C.red} />
        <Stat label="ARPU" value={fmtMoney(d.arpu)} />
        <Stat label="Active Subs" value={d.active_subscriptions} accent={C.green} />
        <Stat label="Failed Payments" value={d.failed_payments} accent={C.red} />
        <Stat label="Refunds" value={d.refunds} accent={C.red} />
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="p-5"><p className="font-semibold mb-4" style={{ color: C.ink }}>Revenue over time</p>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={d.revenue_over_time || []}><XAxis dataKey="month" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} /><Tooltip /><Line type="monotone" dataKey="revenue" stroke={C.gold} strokeWidth={2.5} /></LineChart>
          </ResponsiveContainer>
        </Card>
        <Card className="p-5"><p className="font-semibold mb-4" style={{ color: C.ink }}>Plan breakdown</p>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart><Pie data={planPie} dataKey="value" nameKey="name" outerRadius={80} label>{planPie.map((e, i) => <Cell key={i} fill={e.color} />)}</Pie><Tooltip /></PieChart>
          </ResponsiveContainer>
        </Card>
        <Card className="p-5"><p className="font-semibold mb-4" style={{ color: C.ink }}>Free vs Paid</p>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart><Pie data={pie} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} label>{pie.map((e, i) => <Cell key={i} fill={e.color} />)}</Pie><Tooltip /></PieChart>
          </ResponsiveContainer>
        </Card>
        <Card className="p-5"><p className="font-semibold mb-4" style={{ color: C.ink }}>Revenue by plan</p>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={[{ name: 'Premium', v: d.revenue_by_plan?.premium || 0 }, { name: 'Family', v: d.revenue_by_plan?.family || 0 }]}>
              <XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} /><Tooltip /><Bar dataKey="v" fill={C.navy2} radius={[6, 6, 0, 0]} /></BarChart>
          </ResponsiveContainer>
        </Card>
      </div>
    </div>
  );
}

// ---------- Payments ----------
function Payments() {
  const { data, loading } = useData('/payments');
  if (loading) return <Loading />;
  const d = data || {}; const s = d.summary || {};
  return (
    <div className="space-y-4" data-testid="admin-payments">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Successful" value={s.successful || 0} accent={C.green} />
        <Stat label="Failed" value={s.failed || 0} accent={C.red} />
        <Stat label="Refunds" value={s.refunds || 0} accent={C.gold} />
        <Stat label="Initiated" value={s.initiated || 0} accent={C.mute} />
      </div>
      {(!d.payments || d.payments.length === 0) ? <Empty text="No payment transactions yet." /> : (
        <Card className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[#8D8880] border-b border-[#E8EAEE]">{['User', 'Plan', 'Amount', 'Status', 'Date'].map((h) => <th key={h} className="px-4 py-3 font-medium">{h}</th>)}</tr></thead>
            <tbody>{d.payments.map((p, i) => (
              <tr key={i} className="border-b border-[#F1F2F4]"><td className="px-4 py-3">{p.user_email || p.user_id}</td><td className="px-4 py-3 capitalize">{p.plan_id}</td><td className="px-4 py-3">{fmtMoney(p.amount)}</td><td className="px-4 py-3">{badge(p.payment_status, statusColor(p.payment_status))}</td><td className="px-4 py-3">{fmtDate(p.created_at)}</td></tr>
            ))}</tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

// ---------- Errors ----------
function Errors({ me }) {
  const { data, loading, reload } = useData('/errors');
  const canEdit = me?.caps?.includes('*');
  if (loading) return <Loading />;
  const errs = data?.errors || [];
  const update = async (id, body) => { try { await adminAxios().patch(`/errors/${id}`, body); toast.success('Updated'); reload(); } catch (e) { toast.error(apiError(e)); } };

  const active = errs.filter((e) => e.status !== 'fixed');
  const completed = errs
    .filter((e) => e.status === 'fixed')
    .sort((a, b) => new Date(b.fixed_at || b.created_at) - new Date(a.fixed_at || a.created_at));

  // Group completed jobs by completion date (newest first).
  const groups = [];
  const byDate = {};
  completed.forEach((e) => {
    const d = e.fixed_at || e.created_at;
    const key = d ? new Date(d).toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : 'Unknown date';
    if (!byDate[key]) { byDate[key] = []; groups.push(key); }
    byDate[key].push(e);
  });

  const ErrorCard = ({ e, done }) => (
    <Card key={e.error_id} className="p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-mono text-sm break-words" style={{ color: done ? C.mute : '#C0492E' }}>{e.message}</p>
          <p className="text-xs text-[#8D8880] mt-1">
            {e.method} {e.location} · logged {fmtDateTime(e.created_at)}
            {done && e.fixed_at && <> · <span style={{ color: C.green }}>completed {fmtDateTime(e.fixed_at)}</span></>}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">{badge(e.status, statusColor(e.status))}{e.priority && badge(e.priority, C.gold)}</div>
      </div>
      {canEdit && (
        <div className="flex gap-2 mt-3">
          {done ? (
            <button onClick={() => update(e.error_id, { status: 'investigating' })}
              className="text-xs px-2.5 py-1 rounded-full border" style={{ borderColor: C.line, color: C.navy }}>
              Reopen
            </button>
          ) : (
            ['new', 'investigating', 'fixed'].map((s) => (
              <button key={s} onClick={() => update(e.error_id, { status: s })}
                className="text-xs px-2.5 py-1 rounded-full border capitalize"
                style={{ borderColor: e.status === s ? C.navy : C.line, background: e.status === s ? C.navy : 'transparent', color: e.status === s ? '#fff' : C.navy }}>
                {s === 'fixed' ? 'Mark fixed' : s}
              </button>
            ))
          )}
        </div>
      )}
    </Card>
  );

  return (
    <div className="space-y-8" data-testid="admin-errors">
      {/* Active problems */}
      <div className="space-y-3">
        <h3 className="text-sm font-bold uppercase tracking-wide" style={{ color: C.navy }} data-testid="errors-active-heading">
          Open problems ({active.length})
        </h3>
        {active.length === 0
          ? <Empty text="No open problems. 🎉" />
          : active.map((e) => <ErrorCard key={e.error_id} e={e} done={false} />)}
      </div>

      {/* Completed jobs grouped by date */}
      <div className="space-y-4" data-testid="errors-completed-section">
        <h3 className="text-sm font-bold uppercase tracking-wide" style={{ color: C.green }} data-testid="errors-completed-heading">
          Completed ({completed.length})
        </h3>
        {completed.length === 0 ? (
          <Empty text="No completed jobs yet." />
        ) : (
          groups.map((day) => (
            <div key={day} className="space-y-3" data-testid={`errors-completed-group`}>
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold px-2.5 py-1 rounded-full" style={{ background: '#EAF5EE', color: C.green }}>{day}</span>
                <span className="text-xs text-[#8D8880]">{byDate[day].length} job{byDate[day].length > 1 ? 's' : ''}</span>
              </div>
              {byDate[day].map((e) => <ErrorCard key={e.error_id} e={e} done={true} />)}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ---------- Security ----------
function Security({ me }) {
  const { data, loading, reload } = useData('/security');
  const isOwner = me?.caps?.includes('*');
  if (loading) return <Loading />;
  const d = data || {};
  const blockIp = async () => { const ip = window.prompt('IP address to block:'); if (!ip) return; try { await adminAxios().post('/security/block-ip', { ip, reason: 'manual' }); toast.success('IP blocked'); reload(); } catch (e) { toast.error(apiError(e)); } };
  const unblock = async (ip) => { try { await adminAxios().post('/security/unblock-ip', { ip }); toast.success('Unblocked'); reload(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div className="space-y-4" data-testid="admin-security">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Failed Logins" value={d.failed_logins?.length || 0} accent={C.red} />
        <Stat label="Suspicious IPs" value={d.suspicious_ips?.length || 0} accent={C.gold} />
        <Stat label="Blocked IPs" value={d.blocked_ips?.length || 0} accent={C.red} />
        <Stat label="Admin Sessions" value={d.admin_sessions?.length || 0} accent={C.navy} />
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="p-5">
          <div className="flex items-center justify-between mb-3"><p className="font-semibold" style={{ color: C.ink }}>Blocked IPs</p>{isOwner && <button onClick={blockIp} className="text-xs px-3 py-1.5 rounded-full text-white" style={{ background: C.navy }}>Block IP</button>}</div>
          {(d.blocked_ips || []).length === 0 ? <p className="text-sm text-[#8D8880]">None blocked.</p> : d.blocked_ips.map((b) => (
            <div key={b.ip} className="flex items-center justify-between py-1.5 text-sm"><span>{b.ip} <span className="text-[#8D8880]">· {b.reason}</span></span>{isOwner && <button onClick={() => unblock(b.ip)} className="text-xs" style={{ color: C.navy2 }}>Unblock</button>}</div>
          ))}
        </Card>
        <Card className="p-5"><p className="font-semibold mb-3" style={{ color: C.ink }}>Recent failed logins</p>
          {(d.failed_logins || []).length === 0 ? <p className="text-sm text-[#8D8880]">No failed attempts.</p> : d.failed_logins.slice(0, 8).map((f, i) => (
            <div key={i} className="text-sm py-1.5 border-b border-[#F1F2F4] last:border-0"><span className="text-[#57534E]">{f.email}</span> <span className="text-[#8D8880]">· {f.ip} · {f.reason} · {fmtDateTime(f.timestamp)}</span></div>
          ))}
        </Card>
        <Card className="p-5"><p className="font-semibold mb-3" style={{ color: C.ink }}>Admin login history</p>
          {(d.admin_login_history || []).slice(0, 8).map((a, i) => (
            <div key={i} className="text-sm py-1.5 border-b border-[#F1F2F4] last:border-0">{a.email} <span className="text-[#8D8880]">· {a.ip} · {fmtDateTime(a.timestamp)}</span></div>
          ))}
        </Card>
        <Card className="p-5"><p className="font-semibold mb-3" style={{ color: C.ink }}>Password reset requests</p>
          {(d.password_reset_requests || []).length === 0 ? <p className="text-sm text-[#8D8880]">None.</p> : d.password_reset_requests.slice(0, 8).map((r, i) => (
            <div key={i} className="text-sm py-1.5">{r.email} <span className="text-[#8D8880]">· {fmtDateTime(r.created_at)}</span></div>
          ))}
        </Card>
      </div>
    </div>
  );
}

// ---------- Content Monitoring ----------
function ContentMonitoring() {
  const { data, loading } = useData('/content-monitoring');
  const [confirm, setConfirm] = useState(false);
  if (loading) return <Loading />;
  const d = data || {};
  return (
    <div className="space-y-4" data-testid="admin-content-monitoring">
      <div className="rounded-2xl p-4 text-sm flex items-start gap-2" style={{ background: '#FCF6E9', border: `1px solid ${C.gold}55`, color: '#8A6D1F' }}>
        <Shield className="h-4 w-4 mt-0.5 shrink-0" /> Private user files are never shown here. Only aggregate storage activity is displayed to respect user privacy.
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Stat label="Total Uploads" value={d.total_uploads || 0} accent={C.navy} />
        <Stat label="Documents" value={d.by_type?.documents || 0} />
        <Stat label="Photos" value={d.by_type?.photos || 0} />
        <Stat label="Videos" value={d.by_type?.videos || 0} />
        <Stat label="Voice" value={d.by_type?.voice || 0} />
      </div>
      <Card className="p-5">
        <p className="font-semibold mb-3" style={{ color: C.ink }}>Storage by account (top 25)</p>
        {(d.per_account || []).length === 0 ? <Empty text="No uploads yet." /> : (
          <div className="space-y-1">{d.per_account.map((a, i) => (
            <div key={i} className="flex items-center justify-between text-sm py-1.5 border-b border-[#F1F2F4] last:border-0">
              <span>{a.name} <span className="text-[#8D8880]">· {a.email}</span></span><span className="font-semibold">{a.storage_used} files</span>
            </div>
          ))}</div>
        )}
      </Card>
    </div>
  );
}

// ---------- Partners ----------
function Partners({ me }) {
  const { data, loading, reload } = useData('/partners');
  const canEdit = allowed(me, 'edit_partners');
  const isOwner = me?.caps?.includes('*');
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: '', contact_person: '', email: '', phone: '', partner_type: 'estate_attorney' });
  if (loading) return <Loading />;
  const partners = data?.partners || [];
  const create = async () => {
    if (!form.name) { toast.error('Partner name required'); return; }
    try { await adminAxios().post('/partners', form); toast.success('Partner added'); setAdding(false); setForm({ name: '', contact_person: '', email: '', phone: '', partner_type: 'estate_attorney' }); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => { if (!window.confirm('Remove this partner?')) return; try { await adminAxios().delete(`/partners/${id}`); toast.success('Removed'); reload(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div className="space-y-4" data-testid="admin-partners">
      <div className="flex justify-between">
        <button onClick={() => downloadWithAuth('/partners/export/csv', 'partners.csv')} className="h-10 px-4 rounded-xl border text-sm font-medium flex items-center gap-2" style={{ borderColor: C.line, color: C.navy }}><Download className="h-4 w-4" /> Export</button>
        {canEdit && <button data-testid="admin-add-partner" onClick={() => setAdding(true)} className="h-10 px-4 rounded-xl text-white text-sm font-medium flex items-center gap-2" style={{ background: C.navy }}><Plus className="h-4 w-4" /> Add Partner</button>}
      </div>
      {partners.length === 0 ? <Empty text="No partners yet. Add estate attorneys, funeral homes, advisors and more." /> : (
        <Card className="overflow-x-auto"><table className="w-full text-sm">
          <thead><tr className="text-left text-[#8D8880] border-b border-[#E8EAEE]">{['Partner', 'Type', 'Referral Code', 'Referrals', 'Conversions', 'Commission', 'Status', ''].map((h) => <th key={h} className="px-4 py-3 font-medium">{h}</th>)}</tr></thead>
          <tbody>{partners.map((p) => (
            <tr key={p.partner_id} className="border-b border-[#F1F2F4]">
              <td className="px-4 py-3"><p className="font-medium">{p.name}</p><p className="text-xs text-[#8D8880]">{p.contact_person} · {p.email}</p></td>
              <td className="px-4 py-3 capitalize">{(p.partner_type || '').replace(/_/g, ' ')}</td>
              <td className="px-4 py-3 font-mono">{p.referral_code}</td>
              <td className="px-4 py-3">{p.referrals}</td><td className="px-4 py-3">{p.conversions}</td><td className="px-4 py-3">{fmtMoney(p.commission_owed)}</td>
              <td className="px-4 py-3">{badge(p.status, statusColor(p.status))}</td>
              <td className="px-4 py-3">{isOwner && <button onClick={() => remove(p.partner_id)} className="text-[#C0492E]"><Trash2 className="h-4 w-4" /></button>}</td>
            </tr>
          ))}</tbody>
        </table></Card>
      )}
      {adding && (
        <Modal title="Add Partner" onClose={() => setAdding(false)}>
          <div className="space-y-3">
            {[['name', 'Organization name'], ['contact_person', 'Contact person'], ['email', 'Email'], ['phone', 'Phone']].map(([k, ph]) => (
              <input key={k} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} placeholder={ph} className="w-full h-11 px-3 rounded-xl border border-[#E3E1DC] text-sm outline-none" />
            ))}
            <select value={form.partner_type} onChange={(e) => setForm({ ...form, partner_type: e.target.value })} className="w-full h-11 px-3 rounded-xl border border-[#E3E1DC] text-sm">
              {['estate_attorney', 'funeral_home', 'financial_advisor', 'insurance_agent', 'church', 'hospice'].map((t) => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
            </select>
            <button onClick={create} className="w-full h-11 rounded-xl text-white font-semibold" style={{ background: C.navy }}>Create partner</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ---------- Leads ----------
function Leads({ me }) {
  const { data, loading, reload } = useData('/leads');
  const canEdit = allowed(me, 'edit_leads');
  if (loading) return <Loading />;
  const leads = data?.leads || [];
  const setStatus = async (l, status) => { try { await adminAxios().patch(`/leads/${l.id}`, { source: l.source, status }); toast.success('Updated'); reload(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div className="space-y-4" data-testid="admin-leads">
      <div className="flex justify-end"><button onClick={() => downloadWithAuth('/leads/export/csv', 'leads.csv')} className="h-10 px-4 rounded-xl border text-sm font-medium flex items-center gap-2" style={{ borderColor: C.line, color: C.navy }}><Download className="h-4 w-4" /> Export</button></div>
      {leads.length === 0 ? <Empty text="No leads or form submissions yet." /> : (
        <Card className="overflow-x-auto"><table className="w-full text-sm">
          <thead><tr className="text-left text-[#8D8880] border-b border-[#E8EAEE]">{['Name', 'Email', 'Source', 'Message', 'Date', 'Status', ''].map((h) => <th key={h} className="px-4 py-3 font-medium">{h}</th>)}</tr></thead>
          <tbody>{leads.map((l, i) => (
            <tr key={i} className="border-b border-[#F1F2F4]">
              <td className="px-4 py-3">{l.name}</td><td className="px-4 py-3">{l.email}</td>
              <td className="px-4 py-3">{badge((l.source || '').replace(/_/g, ' '), C.navy2)}</td>
              <td className="px-4 py-3 max-w-[220px] truncate text-[#57534E]">{l.message}</td>
              <td className="px-4 py-3 whitespace-nowrap">{fmtDate(l.date)}</td>
              <td className="px-4 py-3">{badge(l.status, statusColor(l.status))}</td>
              <td className="px-4 py-3">{canEdit && l.status !== 'contacted' && <button onClick={() => setStatus(l, 'contacted')} className="text-xs font-semibold" style={{ color: C.green }}>Mark contacted</button>}</td>
            </tr>
          ))}</tbody>
        </table></Card>
      )}
    </div>
  );
}

// ---------- Support ----------
function Support({ me }) {
  const { data, loading, reload } = useData('/tickets');
  const canEdit = allowed(me, 'edit_support');
  const [open, setOpen] = useState(null);
  const [reply, setReply] = useState('');
  if (loading) return <Loading />;
  const tickets = data?.tickets || [];
  const send = async () => { if (!reply.trim()) return; try { await adminAxios().post(`/tickets/${open.ticket_id}/reply`, { message: reply }); toast.success('Reply sent'); setReply(''); reload(); setOpen(null); } catch (e) { toast.error(apiError(e)); } };
  const setStatus = async (t, status) => { try { await adminAxios().patch(`/tickets/${t.ticket_id}`, { status }); toast.success('Updated'); reload(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div className="space-y-4" data-testid="admin-support">
      {tickets.length === 0 ? <Empty text="No support tickets yet." /> : (
        <Card className="overflow-x-auto"><table className="w-full text-sm">
          <thead><tr className="text-left text-[#8D8880] border-b border-[#E8EAEE]">{['Ticket', 'User', 'Subject', 'Priority', 'Status', 'Updated', ''].map((h) => <th key={h} className="px-4 py-3 font-medium">{h}</th>)}</tr></thead>
          <tbody>{tickets.map((t) => (
            <tr key={t.ticket_id} className="border-b border-[#F1F2F4]">
              <td className="px-4 py-3 font-mono">{t.ticket_number}</td><td className="px-4 py-3">{t.user_email}</td>
              <td className="px-4 py-3">{t.subject}</td><td className="px-4 py-3 capitalize">{t.priority}</td>
              <td className="px-4 py-3">{badge(t.status, statusColor(t.status))}</td><td className="px-4 py-3">{fmtDate(t.updated_at)}</td>
              <td className="px-4 py-3"><button onClick={() => setOpen(t)} className="text-xs font-semibold" style={{ color: C.navy2 }}>Open</button></td>
            </tr>
          ))}</tbody>
        </table></Card>
      )}
      {open && (
        <Modal title={`${open.ticket_number} · ${open.subject}`} onClose={() => setOpen(null)}>
          <div className="space-y-2 max-h-64 overflow-y-auto mb-3">
            {(open.messages || []).map((m, i) => (
              <div key={i} className={`text-sm rounded-xl px-3 py-2 ${m.role === 'admin' ? 'bg-[#EEF3F9] ml-6' : 'bg-[#F5F4F2] mr-6'}`}>
                <p className="text-xs text-[#8D8880] mb-0.5">{m.by} · {fmtDateTime(m.at)}{m.internal ? ' · internal' : ''}</p>{m.message}
              </div>
            ))}
          </div>
          {canEdit && (
            <>
              <textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={3} placeholder="Type a reply…" className="w-full rounded-xl border border-[#E3E1DC] p-3 text-sm outline-none" />
              <div className="flex gap-2 mt-2">
                <button onClick={send} className="flex-1 h-10 rounded-xl text-white text-sm font-semibold" style={{ background: C.navy }}>Send reply</button>
                <button onClick={() => setStatus(open, open.status === 'closed' ? 'open' : 'closed')} className="h-10 px-4 rounded-xl border text-sm" style={{ borderColor: C.line }}>{open.status === 'closed' ? 'Reopen' : 'Close'}</button>
              </div>
            </>
          )}
        </Modal>
      )}
    </div>
  );
}

// ---------- Site Content ----------
function SiteContent({ me }) {
  const { data, loading } = useData('/content');
  const isOwner = me?.caps?.includes('*');
  const [form, setForm] = useState(null);
  useEffect(() => { if (data) setForm({ ...data, social: data.social || {} }); }, [data]);
  if (loading || !form) return <Loading />;
  const save = async () => { try { await adminAxios().put('/content', form); toast.success('Content saved'); } catch (e) { toast.error(apiError(e)); } };
  const fld = (k, label, ph) => (
    <div><label className="block text-xs font-semibold text-[#57534E] mb-1">{label}</label>
      <input disabled={!isOwner} value={form[k] || ''} onChange={(e) => setForm({ ...form, [k]: e.target.value })} placeholder={ph} className="w-full h-11 px-3 rounded-xl border border-[#E3E1DC] text-sm outline-none disabled:bg-[#F7F8FA]" /></div>
  );
  return (
    <div className="space-y-4 max-w-2xl" data-testid="admin-site-content">
      {!isOwner && <p className="text-sm text-[#8D8880]">Read-only — only the Owner can edit site content.</p>}
      <Card className="p-5 space-y-3">
        <div><label className="block text-xs font-semibold text-[#57534E] mb-1">Homepage announcement</label>
          <textarea disabled={!isOwner} value={form.homepage_announcement || ''} onChange={(e) => setForm({ ...form, homepage_announcement: e.target.value })} rows={2} className="w-full rounded-xl border border-[#E3E1DC] p-3 text-sm outline-none disabled:bg-[#F7F8FA]" /></div>
        {fld('pricing_text', 'Pricing plan text')}
        {fld('partner_program_text', 'Partner program text')}
        {fld('checklist_link', 'Checklist download link')}
        {fld('contact_email', 'Contact email')}
        <div className="grid grid-cols-2 gap-3">
          {['facebook', 'instagram', 'twitter', 'linkedin'].map((s) => (
            <div key={s}><label className="block text-xs font-semibold text-[#57534E] mb-1 capitalize">{s}</label>
              <input disabled={!isOwner} value={form.social?.[s] || ''} onChange={(e) => setForm({ ...form, social: { ...form.social, [s]: e.target.value } })} className="w-full h-11 px-3 rounded-xl border border-[#E3E1DC] text-sm outline-none disabled:bg-[#F7F8FA]" /></div>
          ))}
        </div>
        {isOwner && <button onClick={save} className="h-11 px-6 rounded-xl text-white font-semibold" style={{ background: C.navy }}>Save changes</button>}
      </Card>
    </div>
  );
}

// ---------- Email Delivery (stats only — content is confidential) ----------
function _deliveryHealth(s) {
  const sent = s.sent || 0;
  if (sent === 0) return { label: 'No activity', color: '#8D8880', bg: '#F1F2F4', rate: null };
  const bounceRate = (s.bounced || 0) / sent;
  const spamRate = (s.spam || 0) / sent;
  const pct = (bounceRate * 100).toFixed(1);
  if (bounceRate > 0.05 || spamRate > 0.005)
    return { label: 'Needs attention', color: '#B91C1C', bg: '#FEF2F2', rate: pct };
  if (bounceRate > 0.02)
    return { label: 'Watch', color: '#B45309', bg: '#FFFBEB', rate: pct };
  return { label: 'Healthy', color: '#15803D', bg: '#F0FDF4', rate: pct };
}

function EmailTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    adminAxios().get('/email-templates')
      .then((r) => setTemplates(r.data.templates))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  if (loading) return <Loading />;

  return (
    <div className="space-y-4 max-w-3xl" data-testid="admin-email-templates">
      <p className="text-sm text-[#8D8880]">
        Delivery activity for automated emails. To protect member privacy, the subject lines,
        preview text and message contents are confidential and are never shown here.
      </p>
      {templates.map((t) => {
        const h = _deliveryHealth(t.stats);
        return (
          <Card key={t.template_id} className="p-5" data-testid={`email-tpl-${t.template_id}`}>
            <div className="flex items-start justify-between gap-3 mb-1">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4" style={{ color: C.navy }} />
                <span className="font-semibold" style={{ color: C.ink }}>{t.name}</span>
              </div>
              <span
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap"
                style={{ background: h.bg, color: h.color }}
                title={h.rate !== null ? `Bounce rate ${h.rate}%` : 'No emails sent yet'}
                data-testid={`email-tpl-health-${t.template_id}`}
              >
                <span className="w-2 h-2 rounded-full" style={{ background: h.color }} />
                {h.label}{h.rate !== null && Number(h.rate) > 0 ? ` · ${h.rate}% bounce` : ''}
              </span>
            </div>
            <p className="text-xs text-[#8D8880] mb-4">{t.description}</p>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid={`email-tpl-stats-${t.template_id}`}>
              {[['Sent', t.stats.sent, C.navy], ['Delivered', t.stats.delivered, C.green],
                ['Bounced', t.stats.bounced, C.red], ['Spam', t.stats.spam, '#B45309'],
                ['Failed', t.stats.failed, C.red]].map(([label, val, color]) => (
                <div key={label} className="bg-[#F7F8FA] border border-[#E3E1DC] rounded-xl p-3 text-center">
                  <div className="text-lg font-bold" style={{ color }}>{val}</div>
                  <div className="text-[11px] text-[#8D8880]">{label}</div>
                </div>
              ))}
            </div>
          </Card>
        );
      })}
    </div>
  );
}

// ---------- Reports ----------
function Reports() {
  const reports = [
    ['Subscriber list', '/users/export/csv', 'subscribers.csv'],
    ['Partner referrals', '/partners/export/csv', 'partners.csv'],
    ['Leads', '/leads/export/csv', 'leads.csv'],
  ];
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-reports">
      {reports.map(([label, path, file]) => (
        <Card key={path} className="p-5 flex items-center justify-between">
          <div><p className="font-semibold" style={{ color: C.ink }}>{label}</p><p className="text-xs text-[#8D8880]">CSV export</p></div>
          <button onClick={() => downloadWithAuth(path, file)} className="h-10 px-4 rounded-xl text-white text-sm font-medium flex items-center gap-2" style={{ background: C.navy }}><Download className="h-4 w-4" /> Download</button>
        </Card>
      ))}
    </div>
  );
}

// ---------- Settings ----------
function SettingsSection({ me }) {
  const isOwner = me?.caps?.includes('*');
  const [admins, setAdmins] = useState([]);
  const [audit, setAudit] = useState([]);
  const [pw, setPw] = useState({ current_password: '', new_password: '' });
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ email: '', name: '', password: '', role: 'support' });

  const load = () => {
    if (isOwner) adminAxios().get('/admins').then((r) => setAdmins(r.data.admins)).catch(() => {});
    adminAxios().get('/audit').then((r) => setAudit(r.data.logs)).catch(() => {});
  };
  useEffect(load, [isOwner]);

  const changePw = async () => { try { await adminAxios().post('/change-password', pw); toast.success('Password changed'); setPw({ current_password: '', new_password: '' }); } catch (e) { toast.error(apiError(e)); } };
  const createAdmin = async () => { try { await adminAxios().post('/admins', form); toast.success('Admin created'); setAdding(false); setForm({ email: '', name: '', password: '', role: 'support' }); load(); } catch (e) { toast.error(apiError(e)); } };
  const setRole = async (a, role) => { try { await adminAxios().patch(`/admins/${a.admin_id}/role`, { role }); toast.success('Role updated'); load(); } catch (e) { toast.error(apiError(e)); } };
  const removeAdmin = async (a) => { if (!window.confirm(`Remove admin ${a.email}?`)) return; try { await adminAxios().delete(`/admins/${a.admin_id}`); toast.success('Removed'); load(); } catch (e) { toast.error(apiError(e)); } };

  return (
    <div className="space-y-4 max-w-3xl" data-testid="admin-settings">
      <Card className="p-5">
        <p className="font-semibold mb-3" style={{ color: C.ink }}>Change your password</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <input type="password" placeholder="Current password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} className="h-11 px-3 rounded-xl border border-[#E3E1DC] text-sm outline-none" />
          <input type="password" placeholder="New password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className="h-11 px-3 rounded-xl border border-[#E3E1DC] text-sm outline-none" />
        </div>
        <button onClick={changePw} className="mt-3 h-10 px-5 rounded-xl text-white text-sm font-semibold" style={{ background: C.navy }}>Update password</button>
        <p className="text-xs text-[#8D8880] mt-2">2FA: {me?.two_factor_enabled ? 'Enabled ✓' : 'Disabled'}</p>
      </Card>

      {isOwner && (
        <Card className="p-5">
          <div className="flex items-center justify-between mb-3"><p className="font-semibold" style={{ color: C.ink }}>Admin users</p>
            <button onClick={() => setAdding(true)} className="text-xs px-3 py-1.5 rounded-full text-white flex items-center gap-1" style={{ background: C.navy }}><Plus className="h-3.5 w-3.5" /> Add admin</button></div>
          <div className="space-y-2">{admins.map((a) => (
            <div key={a.admin_id} className="flex items-center justify-between text-sm py-2 border-b border-[#F1F2F4] last:border-0">
              <div><p className="font-medium">{a.name} <span className="text-[#8D8880]">· {a.email}</span></p><p className="text-xs text-[#8D8880]">2FA {a.two_factor_enabled ? 'on' : 'off'} · {a.status}</p></div>
              <div className="flex items-center gap-2">
                <select value={a.role} onChange={(e) => setRole(a, e.target.value)} disabled={a.admin_id === me.admin_id} className="text-xs h-8 px-2 rounded-lg border border-[#E3E1DC] capitalize">
                  {['owner', 'manager', 'support'].map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
                {a.admin_id !== me.admin_id && <button onClick={() => removeAdmin(a)} className="text-[#C0492E]"><Trash2 className="h-4 w-4" /></button>}
              </div>
            </div>
          ))}</div>
        </Card>
      )}

      <Card className="p-5">
        <p className="font-semibold mb-3" style={{ color: C.ink }}>Audit log</p>
        <div className="space-y-1 max-h-72 overflow-y-auto">{audit.map((l, i) => (
          <div key={i} className="text-sm py-1.5 border-b border-[#F1F2F4] last:border-0">
            <span className="font-medium">{l.admin_email}</span> <span className="text-[#57534E]">{l.action.replace(/_/g, ' ')}</span>
            {l.target && <span className="text-[#8D8880]"> · {l.target}</span>}{l.detail && <span className="text-[#8D8880]"> · {l.detail}</span>}
            <span className="text-[#B5B0A8] float-right">{fmtDateTime(l.timestamp)}</span>
          </div>
        ))}</div>
      </Card>

      {adding && (
        <Modal title="Add Admin" onClose={() => setAdding(false)}>
          <div className="space-y-3">
            <input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full h-11 px-3 rounded-xl border border-[#E3E1DC] text-sm outline-none" />
            <input placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full h-11 px-3 rounded-xl border border-[#E3E1DC] text-sm outline-none" />
            <input placeholder="Temp password (10+ chars, mixed)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full h-11 px-3 rounded-xl border border-[#E3E1DC] text-sm outline-none" />
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full h-11 px-3 rounded-xl border border-[#E3E1DC] text-sm capitalize">
              {['manager', 'support', 'owner'].map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <button onClick={createAdmin} className="w-full h-11 rounded-xl text-white font-semibold" style={{ background: C.navy }}>Create admin</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ---------- Ambassadors ----------
function Ambassadors({ me }) {
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const isOwner = me?.caps?.includes('*');

  const load = useCallback(() => {
    setLoading(true);
    adminAxios().get('/ambassadors', { params: { status, search } })
      .then((r) => setRows(r.data.ambassadors)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  }, [status, search]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const setStatusFor = async (a, s) => { try { await adminAxios().patch(`/ambassadors/${a.ambassador_id}/status`, { status: s }); toast.success(`Status → ${s}`); load(); setDetail(null); } catch (e) { toast.error(apiError(e)); } };
  const approve = async (a) => { try { await adminAxios().post(`/ambassadors/${a.ambassador_id}/approve`); toast.success('Approved — set-password email sent'); load(); setDetail(null); } catch (e) { toast.error(apiError(e)); } };
  const moderatePic = async (a, action) => { try { await adminAxios().patch(`/ambassadors/${a.ambassador_id}/profile-picture`, { action }); toast.success('Photo ' + action); load(); setDetail(null); } catch (e) { toast.error(apiError(e)); } };
  const [sendingSummaries, setSendingSummaries] = useState(false);
  const sendSummaries = async () => {
    if (!window.confirm('Email a monthly earnings summary to all approved ambassadors now?')) return;
    setSendingSummaries(true);
    try { const r = await adminAxios().post('/ambassadors/send-monthly-summary'); toast.success(`Summaries sent: ${r.data.sent} · failed: ${r.data.failed}`); }
    catch (e) { toast.error(apiError(e)); }
    finally { setSendingSummaries(false); }
  };

  return (
    <div className="space-y-4" data-testid="admin-ambassadors">
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9C948B]" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search ambassadors…" className="w-full h-10 pl-9 pr-3 rounded-xl border border-[#E3E1DC] text-sm outline-none" data-testid="amb-admin-search" />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-10 px-3 rounded-xl border border-[#E3E1DC] text-sm" data-testid="amb-admin-filter">
          {['all', 'pending', 'approved', 'suspended', 'rejected'].map((s) => <option key={s} value={s} className="capitalize">{s}</option>)}
        </select>
        <button onClick={() => downloadWithAuth('/ambassadors/export/csv', 'ambassadors.csv')} className="h-10 px-4 rounded-xl text-white text-sm font-medium flex items-center gap-2" style={{ background: C.navy }}><Download className="h-4 w-4" /> CSV</button>
        {isOwner && <button onClick={sendSummaries} disabled={sendingSummaries} className="h-10 px-4 rounded-xl border text-sm font-medium flex items-center gap-2 disabled:opacity-60" style={{ borderColor: C.line, color: C.navy }} data-testid="admin-send-summaries"><Mail className="h-4 w-4" /> {sendingSummaries ? 'Sending…' : 'Email monthly summaries'}</button>}
      </div>
      {loading ? <Loading /> : rows.length === 0 ? <Empty text="No ambassadors yet." /> : (
        <Card className="overflow-x-auto"><table className="w-full text-sm">
          <thead><tr className="text-left text-[#8D8880] border-b border-[#E8EAEE]">{['Ambassador', 'Status', 'Code', 'Clicks', 'Free', 'Paid', 'Revenue', 'Commission', ''].map((h) => <th key={h} className="px-4 py-3 font-medium">{h}</th>)}</tr></thead>
          <tbody>{rows.map((a) => (
            <tr key={a.ambassador_id} className="border-b border-[#F1F2F4]" data-testid={`amb-row-${a.ambassador_id}`}>
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  {a.profile_picture_url ? <img src={a.profile_picture_url} alt="" className="w-8 h-8 rounded-full object-cover" /> : <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs" style={{ background: C.navy2 }}>{(a.first_name || '?')[0]}</div>}
                  <div><p className="font-medium">{a.first_name} {a.last_name}</p><p className="text-xs text-[#8D8880]">{a.email}</p></div>
                </div>
              </td>
              <td className="px-4 py-3">{badge(a.status, statusColor(a.status === 'approved' ? 'active' : a.status))}</td>
              <td className="px-4 py-3 font-mono">{a.referral_code || '—'}</td>
              <td className="px-4 py-3">{a.total_clicks}</td><td className="px-4 py-3">{a.total_free_signups}</td><td className="px-4 py-3">{a.total_paid_signups}</td>
              <td className="px-4 py-3">{fmtMoney(a.total_revenue_generated)}</td><td className="px-4 py-3">{fmtMoney(a.total_commission_earned)}</td>
              <td className="px-4 py-3"><button onClick={() => setDetail(a)} className="text-xs font-semibold" style={{ color: C.navy2 }}>Manage</button></td>
            </tr>
          ))}</tbody>
        </table></Card>
      )}
      {detail && (
        <Modal title={`${detail.first_name} ${detail.last_name}`} onClose={() => setDetail(null)}>
          <div className="space-y-1 text-sm text-[#57534E]">
            <p><b>Email:</b> {detail.email} · <b>Phone:</b> {detail.phone || '—'}</p>
            <p><b>Status:</b> <span className="capitalize">{detail.status}</span> · <b>Code:</b> {detail.referral_code || '—'}</p>
            <p><b>Clicks:</b> {detail.total_clicks} · <b>Paid:</b> {detail.total_paid_signups} · <b>Commission earned:</b> {fmtMoney(detail.total_commission_earned)}</p>
            {detail.why && <p className="mt-2"><b>Why:</b> {detail.why}</p>}
            {detail.how && <p><b>How:</b> {detail.how}</p>}
          </div>
          {detail.profile_picture_url && (
            <div className="mt-3 flex items-center gap-3">
              <img src={detail.profile_picture_url} alt="" className="w-16 h-16 rounded-full object-cover" />
              <div className="text-xs">
                <p className="text-[#8D8880]">Photo: {detail.profile_picture_status}</p>
                {isOwner && <div className="flex gap-2 mt-1">
                  <button onClick={() => moderatePic(detail, 'approve')} className="px-2 py-1 rounded-lg border text-xs" style={{ borderColor: C.line, color: C.green }}>Approve</button>
                  <button onClick={() => moderatePic(detail, 'reject')} className="px-2 py-1 rounded-lg border text-xs" style={{ borderColor: C.line, color: C.red }}>Reject</button>
                  <button onClick={() => moderatePic(detail, 'remove')} className="px-2 py-1 rounded-lg border text-xs" style={{ borderColor: C.line }}>Remove</button>
                </div>}
              </div>
            </div>
          )}
          {isOwner && (
            <div className="flex flex-wrap gap-2 mt-4">
              {detail.status !== 'approved' && <ActionBtn onClick={() => approve(detail)} icon={CheckCircle2} color={C.green}>Approve</ActionBtn>}
              {detail.status !== 'suspended' && <ActionBtn onClick={() => setStatusFor(detail, 'suspended')} icon={Ban} color={C.red}>Suspend</ActionBtn>}
              {detail.status !== 'rejected' && <ActionBtn onClick={() => setStatusFor(detail, 'rejected')} icon={XCircle} color={C.red}>Reject</ActionBtn>}
              {detail.status === 'suspended' && <ActionBtn onClick={() => setStatusFor(detail, 'approved')} icon={Unlock}>Reactivate</ActionBtn>}
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}

// ---------- Commissions ----------
function Commissions({ me }) {
  const [status, setStatus] = useState('all');
  const { data, loading, reload } = useData(`/commissions?status=${status}`, [status]);
  const isOwner = me?.caps?.includes('*');
  const act = async (cid, body) => { try { await adminAxios().patch(`/commissions/${cid}`, body); toast.success('Updated'); reload(); } catch (e) { toast.error(apiError(e)); } };
  if (loading) return <Loading />;
  const d = data || {}; const s = d.summary || {};
  return (
    <div className="space-y-4" data-testid="admin-commissions">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[['pending', C.gold], ['approved', C.navy2], ['paid', C.green], ['cancelled', C.mute], ['reversed', C.red]].map(([k, c]) => (
          <Stat key={k} label={k} value={s[k] || 0} accent={c} />
        ))}
      </div>
      <div className="flex gap-3 items-center">
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-10 px-3 rounded-xl border border-[#E3E1DC] text-sm">
          {['all', 'pending', 'approved', 'paid', 'cancelled', 'reversed'].map((x) => <option key={x} value={x} className="capitalize">{x}</option>)}
        </select>
        <button onClick={() => downloadWithAuth('/commissions/export/csv', 'commissions.csv')} className="h-10 px-4 rounded-xl border text-sm font-medium flex items-center gap-2" style={{ borderColor: C.line, color: C.navy }}><Download className="h-4 w-4" /> Export</button>
      </div>
      {(d.commissions || []).length === 0 ? <Empty text="No commissions yet." /> : (
        <Card className="overflow-x-auto"><table className="w-full text-sm">
          <thead><tr className="text-left text-[#8D8880] border-b border-[#E8EAEE]">{['Ambassador', 'Type', 'Payment', 'Commission', 'Status', 'Payable', ''].map((h) => <th key={h} className="px-4 py-3 font-medium">{h}</th>)}</tr></thead>
          <tbody>{d.commissions.map((c) => (
            <tr key={c.commission_id} className="border-b border-[#F1F2F4]">
              <td className="px-4 py-3">{c.ambassador_name}</td>
              <td className="px-4 py-3 capitalize">{c.commission_type.replace(/_/g, ' ')}</td>
              <td className="px-4 py-3">{fmtMoney(c.payment_amount)}</td>
              <td className="px-4 py-3 font-semibold">{fmtMoney(c.commission_amount)}</td>
              <td className="px-4 py-3">{badge(c.commission_status, statusColor(c.commission_status === 'approved' ? 'active' : c.commission_status))}</td>
              <td className="px-4 py-3 whitespace-nowrap">{fmtDate(c.payable_date)}</td>
              <td className="px-4 py-3">{isOwner && c.commission_status === 'pending' && <button onClick={() => act(c.commission_id, { status: 'approved' })} className="text-xs font-semibold" style={{ color: C.green }}>Approve</button>}
                {isOwner && ['pending', 'approved'].includes(c.commission_status) && <button onClick={() => act(c.commission_id, { status: 'cancelled' })} className="text-xs font-semibold ml-2" style={{ color: C.red }}>Cancel</button>}</td>
            </tr>
          ))}</tbody>
        </table></Card>
      )}
    </div>
  );
}

// ---------- Payouts ----------
function Payouts_({ me }) {
  const { data, loading, reload } = useData('/payouts');
  const { data: reqData, reload: reloadReq } = useData('/payout-requests');
  const isOwner = me?.caps?.includes('*');
  if (loading) return <Loading />;
  const d = data || {};
  const create = async (aid) => { try { const r = await adminAxios().post('/payouts/create', { ambassador_id: aid }); toast.success(`Payout created: ${fmtMoney(r.data.amount)}`); reload(); } catch (e) { toast.error(apiError(e)); } };
  const markPaid = async (pid) => { try { await adminAxios().post(`/payouts/${pid}/mark-paid`); toast.success('Marked paid'); reload(); } catch (e) { toast.error(apiError(e)); } };
  const payStripe = async (pid) => { if (!window.confirm('Send this payout to the ambassador via Stripe now?')) return; try { const r = await adminAxios().post(`/payouts/${pid}/pay-stripe`); toast.success(`Sent via Stripe (${r.data.stripe_transfer_id})`); reload(); } catch (e) { toast.error(apiError(e)); } };
  const approveReq = async (rid) => { try { const r = await adminAxios().post(`/payout-requests/${rid}/approve`); toast.success(`Approved — payout ${fmtMoney(r.data.amount)} created`); reloadReq(); reload(); } catch (e) { toast.error(apiError(e)); } };
  const requests = (reqData || {}).requests || [];
  return (
    <div className="space-y-5" data-testid="admin-payouts">
      {requests.length > 0 && (
        <Card className="p-5" data-testid="admin-payout-requests">
          <p className="font-semibold mb-3" style={{ color: C.ink }}>Payout requests <span className="text-xs font-normal text-[#8D8880]">({requests.length} pending)</span></p>
          <div className="space-y-2">{requests.map((r) => (
            <div key={r.payout_request_id} className="flex items-center justify-between text-sm py-2 border-b border-[#F1F2F4] last:border-0">
              <span>{r.ambassador_name} <span className="text-[#8D8880]">· {r.ambassador_email}</span>{!r.stripe_payouts_enabled && <span className="text-xs ml-2" style={{ color: '#C0563F' }}>(Stripe not connected)</span>}</span>
              <span className="flex items-center gap-3"><b>{fmtMoney(r.amount)}</b>{isOwner && <button onClick={() => approveReq(r.payout_request_id)} className="text-xs px-3 py-1.5 rounded-full text-white" style={{ background: C.green }} data-testid="approve-payout-request">Approve</button>}</span>
            </div>
          ))}</div>
        </Card>
      )}
      <Card className="p-5">
        <p className="font-semibold mb-3" style={{ color: C.ink }}>Eligible for payout (approved & 30+ days)</p>
        {(d.eligible || []).length === 0 ? <p className="text-sm text-[#8D8880]">No eligible commissions right now.</p> : (
          <div className="space-y-2">{d.eligible.map((e) => (
            <div key={e.ambassador_id} className="flex items-center justify-between text-sm py-2 border-b border-[#F1F2F4] last:border-0">
              <span>{e.name} <span className="text-[#8D8880]">· {e.payout_method} · {e.payout_email}</span></span>
              <span className="flex items-center gap-3"><b>{fmtMoney(e.amount)}</b>{isOwner && <button onClick={() => create(e.ambassador_id)} className="text-xs px-3 py-1.5 rounded-full text-white" style={{ background: C.navy }}>Create payout</button>}</span>
            </div>
          ))}</div>
        )}
      </Card>
      <Card className="p-5">
        <p className="font-semibold mb-3" style={{ color: C.ink }}>Payout history</p>
        {(d.payouts || []).length === 0 ? <Empty text="No payouts yet." /> : (
          <div className="overflow-x-auto"><table className="w-full text-sm">
            <thead><tr className="text-left text-[#8D8880] border-b border-[#E8EAEE]">{['Payout', 'Amount', 'Method', 'Status', 'Date', ''].map((h) => <th key={h} className="px-4 py-3 font-medium">{h}</th>)}</tr></thead>
            <tbody>{d.payouts.map((p) => (
              <tr key={p.payout_id} className="border-b border-[#F1F2F4]">
                <td className="px-4 py-3 font-mono">{p.payout_id.slice(0, 12)}</td><td className="px-4 py-3 font-semibold">{fmtMoney(p.payout_amount)}</td>
                <td className="px-4 py-3">{p.payout_method}</td><td className="px-4 py-3">{badge(p.payout_status, statusColor(p.payout_status === 'approved' ? 'active' : p.payout_status))}</td>
                <td className="px-4 py-3">{fmtDate(p.payout_date)}</td>
                <td className="px-4 py-3">{isOwner && p.payout_status !== 'paid' && <span className="flex items-center gap-3"><button onClick={() => payStripe(p.payout_id)} className="text-xs font-semibold px-3 py-1.5 rounded-full text-white" style={{ background: C.navy }} data-testid="pay-stripe-btn">Pay via Stripe</button><button onClick={() => markPaid(p.payout_id)} className="text-xs font-semibold" style={{ color: C.mute || '#8D8880' }}>Mark paid</button></span>}</td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </Card>
    </div>
  );
}

// ---------- Modal ----------
function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-lg max-h-[85vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="admin-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold" style={{ color: C.ink }}>{title}</h3>
          <button onClick={onClose}><X className="h-5 w-5 text-[#8D8880]" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}


// ---------- Discount Codes ----------
const D_STATUS_COLORS = { active: '#7FA98C', paused: '#C9A24B', inactive: '#8D8880', expired: '#C0563F' };
const emptyForm = () => ({
  code: '', discount_name: '', discount_description: '', discount_type: 'percentage', discount_value: 10,
  applies_to_plan: 'all', applies_to_billing_cycle: 'all', max_uses: '', max_uses_per_user: 1,
  start_date: '', expiration_date: '', recurring_duration_months: '', individual_user_id: '',
  ambassador_id: '', partner_id: '', internal_notes: '', status: 'active',
});

function Discounts({ me }) {
  const canEdit = me?.caps?.includes('*') || me?.caps?.includes('edit_discounts');
  const { data: stats } = useData('/discounts/stats');
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [redFor, setRedFor] = useState(null);
  const [reports, setReports] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (status !== 'all') params.set('status', status);
    if (search) params.set('search', search);
    adminAxios().get(`/discounts?${params}`).then((r) => setRows(r.data.discounts)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  }, [status, search]);
  useEffect(() => { load(); }, [status]); // eslint-disable-line

  const setStatusOf = async (d, s) => {
    if (s === 'paused' && !window.confirm(`Pause "${d.code}"? Users won't be able to redeem it.`)) return;
    try { await adminAxios().patch(`/discounts/${d.discount_code_id}/status`, { status: s }); toast.success(`${d.code} ${s}`); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const duplicate = async (d) => { try { const r = await adminAxios().post(`/discounts/${d.discount_code_id}/duplicate`); toast.success(`Duplicated as ${r.data.code}`); load(); } catch (e) { toast.error(apiError(e)); } };
  const del = async (d) => { if (!window.confirm(`Permanently delete "${d.code}"? This cannot be undone.`)) return; try { await adminAxios().delete(`/discounts/${d.discount_code_id}`); toast.success('Deleted'); load(); } catch (e) { toast.error(apiError(e)); } };

  const exportCsv = () => {
    if (!rows?.length) return;
    const cols = ['code', 'discount_name', 'discount_type', 'discount_value', 'applies_to_plan', 'applies_to_billing_cycle', 'current_uses', 'max_uses', 'expiration_date', 'effective_status', 'revenue_generated'];
    const csv = [cols.join(',')].concat(rows.map((r) => cols.map((c) => `"${r[c] ?? ''}"`).join(','))).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a'); a.href = url; a.download = 'discount-codes.csv'; a.click(); URL.revokeObjectURL(url);
  };

  const s = stats || {};
  return (
    <div className="space-y-6" data-testid="admin-discounts">
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
        <Stat label="Active codes" value={s.active_codes ?? '—'} accent={C.navy} />
        <Stat label="Redemptions" value={s.total_redemptions ?? '—'} />
        <Stat label="Revenue discounted" value={fmtMoney(s.revenue_discounted || 0)} accent="#C9A24B" />
        <Stat label="New users" value={s.new_users_from_codes ?? '—'} accent="#7FA98C" />
        <Stat label="Group codes" value={s.group_codes ?? '—'} />
        <Stat label="Expiring soon" value={s.expiring_soon ?? '—'} accent="#C0563F" />
        <Stat label="Top code" value={s.most_used?.[0]?.code || '—'} />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8D8880]" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load()} placeholder="Search code or name" className="w-full h-10 pl-9 pr-3 rounded-xl border border-[#E8EAEE] text-sm outline-none" data-testid="discount-search" />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-10 px-3 rounded-xl border border-[#E8EAEE] text-sm" data-testid="discount-status-filter">
          {['all', 'active', 'paused', 'inactive', 'expired'].map((x) => <option key={x} value={x}>{x[0].toUpperCase() + x.slice(1)}</option>)}
        </select>
        <button onClick={exportCsv} className="h-10 px-4 rounded-xl border text-sm font-medium flex items-center gap-2" style={{ borderColor: C.line, color: C.navy }} data-testid="discount-export"><Download className="h-4 w-4" /> CSV</button>
        <button onClick={() => setReports(true)} className="h-10 px-4 rounded-xl border text-sm font-medium flex items-center gap-2" style={{ borderColor: C.line, color: C.navy }} data-testid="discount-reports-btn"><BarChart3 className="h-4 w-4" /> Reports</button>
        {canEdit && <button onClick={() => setEditing(emptyForm())} className="h-10 px-4 rounded-xl text-white text-sm font-medium flex items-center gap-2" style={{ background: C.navy }} data-testid="discount-new"><Plus className="h-4 w-4" /> New code</button>}
      </div>

      <Card className="overflow-hidden">
        {loading ? <Loading /> : !rows?.length ? <Empty text="No discount codes yet." /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[#8D8880] border-b border-[#E8EAEE]">
                {['Code', 'Name', 'Type', 'Value', 'Applies to', 'Cycle', 'Uses', 'Expires', 'Status', 'Revenue', 'Actions'].map((h) => <th key={h} className="px-4 py-3 font-medium whitespace-nowrap">{h}</th>)}
              </tr></thead>
              <tbody>
                {rows.map((d) => (
                  <tr key={d.discount_code_id} className="border-b border-[#F1F2F4]" data-testid={`discount-row-${d.code}`}>
                    <td className="px-4 py-3 font-mono font-bold" style={{ color: C.navy }}>{d.code}</td>
                    <td className="px-4 py-3">{d.discount_name}</td>
                    <td className="px-4 py-3 capitalize">{d.discount_type === 'percentage' ? '%' : '$'}</td>
                    <td className="px-4 py-3 font-semibold">{d.discount_type === 'percentage' ? `${d.discount_value}%` : fmtMoney(d.discount_value)}</td>
                    <td className="px-4 py-3 capitalize">{d.applies_to_plan}</td>
                    <td className="px-4 py-3 capitalize">{d.applies_to_billing_cycle}</td>
                    <td className="px-4 py-3">{d.current_uses}{d.max_uses ? ` / ${d.max_uses}` : ''}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{d.expiration_date ? fmtDate(d.expiration_date) : '—'}</td>
                    <td className="px-4 py-3">{badge(d.effective_status, D_STATUS_COLORS[d.effective_status] || C.mute)}</td>
                    <td className="px-4 py-3 font-semibold">{fmtMoney(d.revenue_generated)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <IconBtn title="Redemptions" onClick={() => setRedFor(d)}><Users className="h-4 w-4" /></IconBtn>
                        {canEdit && <>
                          <IconBtn title="Edit" onClick={() => setEditing({ ...emptyForm(), ...d, max_uses: d.max_uses ?? '', recurring_duration_months: d.recurring_duration_months ?? '', individual_user_id: d.individual_user_id ?? '', start_date: (d.start_date || '').slice(0, 10), expiration_date: (d.expiration_date || '').slice(0, 10), _id: d.discount_code_id })}><Pencil className="h-4 w-4" /></IconBtn>
                          {d.effective_status === 'active' ? <IconBtn title="Pause" onClick={() => setStatusOf(d, 'paused')}><Pause className="h-4 w-4" /></IconBtn> : <IconBtn title="Activate" onClick={() => setStatusOf(d, 'active')}><Play className="h-4 w-4" /></IconBtn>}
                          <IconBtn title="Duplicate" onClick={() => duplicate(d)}><Copy className="h-4 w-4" /></IconBtn>
                          <IconBtn title="Delete" onClick={() => del(d)} color="#C0563F"><Trash2 className="h-4 w-4" /></IconBtn>
                        </>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {editing && <DiscountForm form={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {redFor && <RedemptionsModal d={redFor} onClose={() => setRedFor(null)} />}
      {reports && <ReportsModal onClose={() => setReports(false)} />}
    </div>
  );
}

const IconBtn = ({ onClick, children, title, color }) => (
  <button onClick={onClick} title={title} className="p-1.5 rounded-lg border" style={{ borderColor: C.line, color: color || C.navy }}>{children}</button>
);

function DiscountForm({ form, onClose, onSaved }) {
  const [f, setF] = useState(form);
  const [saving, setSaving] = useState(false);
  const isEdit = !!form._id;
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!f.code.trim() || !f.discount_name.trim()) { toast.error('Code and name are required'); return; }
    setSaving(true);
    const payload = {
      ...f,
      discount_value: Number(f.discount_value),
      max_uses: f.max_uses === '' ? null : Number(f.max_uses),
      max_uses_per_user: f.max_uses_per_user === '' ? null : Number(f.max_uses_per_user),
      recurring_duration_months: f.recurring_duration_months === '' ? null : Number(f.recurring_duration_months),
      start_date: f.start_date || null,
      expiration_date: f.expiration_date || null,
      individual_user_id: f.individual_user_id || null,
    };
    delete payload._id;
    try {
      if (isEdit) await adminAxios().put(`/discounts/${form._id}`, payload);
      else await adminAxios().post('/discounts', payload);
      toast.success(isEdit ? 'Discount updated' : 'Discount created');
      onSaved();
    } catch (e) { toast.error(apiError(e)); setSaving(false); }
  };

  const inp = 'w-full h-10 px-3 rounded-xl border border-[#E8EAEE] text-sm outline-none';
  const Lbl = ({ children }) => <label className="block text-xs font-semibold text-[#57534E] mb-1">{children}</label>;

  return (
    <Modal title={isEdit ? 'Edit discount code' : 'Create discount code'} onClose={onClose}>
      <div className="space-y-3" data-testid="discount-form">
        <div className="grid grid-cols-2 gap-3">
          <div><Lbl>Code</Lbl><input value={f.code} onChange={(e) => set('code', e.target.value.toUpperCase())} className={`${inp} font-mono uppercase`} data-testid="discount-form-code" /></div>
          <div><Lbl>Name</Lbl><input value={f.discount_name} onChange={(e) => set('discount_name', e.target.value)} className={inp} data-testid="discount-form-name" /></div>
        </div>
        <div><Lbl>Description</Lbl><input value={f.discount_description || ''} onChange={(e) => set('discount_description', e.target.value)} className={inp} /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Lbl>Type</Lbl><select value={f.discount_type} onChange={(e) => set('discount_type', e.target.value)} className={inp} data-testid="discount-form-type"><option value="percentage">Percentage (%)</option><option value="fixed_amount">Fixed amount ($)</option></select></div>
          <div><Lbl>{f.discount_type === 'percentage' ? 'Value (%)' : 'Value ($)'}</Lbl><input type="number" value={f.discount_value} onChange={(e) => set('discount_value', e.target.value)} className={inp} data-testid="discount-form-value" /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Lbl>Applies to plan</Lbl><select value={f.applies_to_plan} onChange={(e) => set('applies_to_plan', e.target.value)} className={inp}><option value="all">All plans</option><option value="premium">Premium</option><option value="family">Family</option></select></div>
          <div><Lbl>Billing cycle</Lbl><select value={f.applies_to_billing_cycle} onChange={(e) => set('applies_to_billing_cycle', e.target.value)} className={inp}><option value="all">All</option><option value="monthly">Monthly</option><option value="yearly">Yearly</option></select></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Lbl>Start date</Lbl><input type="date" value={f.start_date || ''} onChange={(e) => set('start_date', e.target.value)} className={inp} /></div>
          <div><Lbl>Expiration date</Lbl><input type="date" value={f.expiration_date || ''} onChange={(e) => set('expiration_date', e.target.value)} className={inp} /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Lbl>Max total uses</Lbl><input type="number" value={f.max_uses} onChange={(e) => set('max_uses', e.target.value)} placeholder="Unlimited" className={inp} /></div>
          <div><Lbl>Max uses per user</Lbl><input type="number" value={f.max_uses_per_user} onChange={(e) => set('max_uses_per_user', e.target.value)} className={inp} /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Lbl>Recurring duration (months)</Lbl><input type="number" value={f.recurring_duration_months} onChange={(e) => set('recurring_duration_months', e.target.value)} placeholder="One-time" className={inp} /></div>
          <div><Lbl>Assign to user ID (optional)</Lbl><input value={f.individual_user_id} onChange={(e) => set('individual_user_id', e.target.value)} placeholder="Any user" className={inp} data-testid="discount-form-user" /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Lbl>Assign to ambassador ID (optional)</Lbl><input value={f.ambassador_id || ''} onChange={(e) => set('ambassador_id', e.target.value)} placeholder="None" className={inp} /></div>
          <div><Lbl>Assign to partner ID (optional)</Lbl><input value={f.partner_id || ''} onChange={(e) => set('partner_id', e.target.value)} placeholder="None" className={inp} /></div>
        </div>
        <div><Lbl>Internal notes</Lbl><textarea value={f.internal_notes || ''} onChange={(e) => set('internal_notes', e.target.value)} rows={2} className="w-full px-3 py-2 rounded-xl border border-[#E8EAEE] text-sm outline-none resize-none" /></div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="h-10 px-5 rounded-xl border font-medium text-sm" style={{ borderColor: C.line, color: C.ink }}>Cancel</button>
          <button onClick={save} disabled={saving} className="h-10 px-6 rounded-xl text-white font-medium text-sm flex items-center gap-2 disabled:opacity-60" style={{ background: C.navy }} data-testid="discount-form-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} {isEdit ? 'Save changes' : 'Create code'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function RedemptionsModal({ d, onClose }) {
  const { data, loading } = useData(`/discounts/${d.discount_code_id}/redemptions`);
  return (
    <Modal title={`Redemptions — ${d.code}`} onClose={onClose}>
      {loading ? <Loading /> : !data?.redemptions?.length ? <Empty text="No redemptions yet." /> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[#8D8880] border-b border-[#E8EAEE]">{['User', 'Plan', 'Paid', 'Status', 'Date'].map((h) => <th key={h} className="px-2 py-2 font-medium">{h}</th>)}</tr></thead>
            <tbody>
              {data.redemptions.map((r) => (
                <tr key={r.redemption_id} className="border-b border-[#F1F2F4]">
                  <td className="px-2 py-2">{r.user_email || r.user_id}</td>
                  <td className="px-2 py-2 capitalize">{r.plan_name} · {r.billing_cycle}</td>
                  <td className="px-2 py-2">{fmtMoney(r.final_price)}</td>
                  <td className="px-2 py-2">{badge(r.status, r.status === 'payment_completed' ? '#7FA98C' : C.mute)}</td>
                  <td className="px-2 py-2">{fmtDate(r.redeemed_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}

// ---------- Groups / Companies ----------
const GROUP_TYPE_LABELS = {
  company_staff: 'Company staff', church_group: 'Church group', veterans_group: 'Veterans group',
  funeral_home_clients: 'Funeral home clients', attorney_clients: 'Attorney clients',
  financial_advisor_clients: 'Financial advisor clients', insurance_agency_clients: 'Insurance agency clients',
  nonprofit_group: 'Nonprofit group', custom_group: 'Custom group',
};
const emptyGroup = () => ({
  company_name: '', group_type: 'company_staff', contact_person: '', contact_email: '', contact_phone: '',
  number_of_eligible_users: 50, start_date: '', expiration_date: '', notes: '', status: 'active',
  discount_code: '', discount_type: 'percentage', discount_value: 25, applies_to_plan: 'all', applies_to_billing_cycle: 'yearly',
});

function Groups({ me }) {
  const canEdit = me?.caps?.includes('*') || me?.caps?.includes('edit_discounts');
  const { data, loading, reload } = useData('/groups');
  const [editing, setEditing] = useState(null);
  const [usersFor, setUsersFor] = useState(null);

  const del = async (g) => { if (!window.confirm(`Delete group "${g.company_name}"? Its discount code will be deactivated.`)) return; try { await adminAxios().delete(`/groups/${g.group_id}`); toast.success('Group deleted'); reload(); } catch (e) { toast.error(apiError(e)); } };
  const s = data?.stats || {};
  return (
    <div className="space-y-6" data-testid="admin-groups">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Total groups" value={s.total_groups ?? '—'} accent={C.navy} />
        <Stat label="Active groups" value={s.active_groups ?? '—'} accent="#7FA98C" />
        <Stat label="Members registered" value={s.total_members ?? '—'} />
        <Stat label="Eligible seats" value={s.eligible_seats ?? '—'} accent="#C9A24B" />
      </div>
      <div className="flex justify-end">
        {canEdit && <button onClick={() => setEditing(emptyGroup())} className="h-10 px-4 rounded-xl text-white text-sm font-medium flex items-center gap-2" style={{ background: C.navy }} data-testid="group-new"><Plus className="h-4 w-4" /> New group</button>}
      </div>
      <Card className="overflow-hidden">
        {loading ? <Loading /> : !data?.groups?.length ? <Empty text="No groups or companies yet." /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[#8D8880] border-b border-[#E8EAEE]">{['Company', 'Type', 'Contact', 'Code', 'Members', 'Expires', 'Status', 'Actions'].map((h) => <th key={h} className="px-4 py-3 font-medium whitespace-nowrap">{h}</th>)}</tr></thead>
              <tbody>
                {data.groups.map((g) => (
                  <tr key={g.group_id} className="border-b border-[#F1F2F4]" data-testid={`group-row-${g.group_id}`}>
                    <td className="px-4 py-3 font-semibold" style={{ color: C.ink }}>{g.company_name}</td>
                    <td className="px-4 py-3">{GROUP_TYPE_LABELS[g.group_type] || g.group_type}</td>
                    <td className="px-4 py-3">{g.contact_email || g.contact_person || '—'}</td>
                    <td className="px-4 py-3 font-mono font-bold" style={{ color: C.navy }}>{g.discount_code}</td>
                    <td className="px-4 py-3">{g.users_registered}{g.number_of_eligible_users ? ` / ${g.number_of_eligible_users}` : ''}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{g.expiration_date ? fmtDate(g.expiration_date) : '—'}</td>
                    <td className="px-4 py-3">{badge(g.status, g.status === 'active' ? '#7FA98C' : C.mute)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <IconBtn title="Members" onClick={() => setUsersFor(g)}><Users className="h-4 w-4" /></IconBtn>
                        <IconBtn title="Copy signup link" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/group/${g.discount_code}`); toast.success('Signup link copied'); }}><Copy className="h-4 w-4" /></IconBtn>
                        {canEdit && <>
                          <IconBtn title="Edit" onClick={() => setEditing({ ...emptyGroup(), ...g, start_date: (g.start_date || '').slice(0, 10), expiration_date: (g.expiration_date || '').slice(0, 10), _id: g.group_id })}><Pencil className="h-4 w-4" /></IconBtn>
                          <IconBtn title="Delete" onClick={() => del(g)} color="#C0563F"><Trash2 className="h-4 w-4" /></IconBtn>
                        </>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {editing && <GroupForm form={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />}
      {usersFor && <GroupUsersModal g={usersFor} onClose={() => setUsersFor(null)} />}
    </div>
  );
}

function GroupForm({ form, onClose, onSaved }) {
  const [f, setF] = useState(form);
  const [saving, setSaving] = useState(false);
  const isEdit = !!form._id;
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const inp = 'w-full h-10 px-3 rounded-xl border border-[#E8EAEE] text-sm outline-none';
  const Lbl = ({ children }) => <label className="block text-xs font-semibold text-[#57534E] mb-1">{children}</label>;

  const save = async () => {
    if (!f.company_name.trim() || !f.discount_code.trim()) { toast.error('Company name and code are required'); return; }
    setSaving(true);
    const payload = { ...f, discount_value: Number(f.discount_value), number_of_eligible_users: f.number_of_eligible_users === '' ? null : Number(f.number_of_eligible_users), start_date: f.start_date || null, expiration_date: f.expiration_date || null };
    delete payload._id; delete payload.users_registered; delete payload.discount_code_id;
    try {
      if (isEdit) await adminAxios().put(`/groups/${form._id}`, payload);
      else await adminAxios().post('/groups', payload);
      toast.success(isEdit ? 'Group updated' : 'Group created'); onSaved();
    } catch (e) { toast.error(apiError(e)); setSaving(false); }
  };

  return (
    <Modal title={isEdit ? 'Edit group / company' : 'New group / company'} onClose={onClose}>
      <div className="space-y-3" data-testid="group-form">
        <div><Lbl>Company / group name</Lbl><input value={f.company_name} onChange={(e) => set('company_name', e.target.value)} className={inp} data-testid="group-form-name" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Lbl>Group type</Lbl><select value={f.group_type} onChange={(e) => set('group_type', e.target.value)} className={inp}>{Object.entries(GROUP_TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
          <div><Lbl>Eligible users</Lbl><input type="number" value={f.number_of_eligible_users} onChange={(e) => set('number_of_eligible_users', e.target.value)} className={inp} /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Lbl>Contact person</Lbl><input value={f.contact_person || ''} onChange={(e) => set('contact_person', e.target.value)} className={inp} /></div>
          <div><Lbl>Contact phone</Lbl><input value={f.contact_phone || ''} onChange={(e) => set('contact_phone', e.target.value)} className={inp} /></div>
        </div>
        <div><Lbl>Contact email</Lbl><input value={f.contact_email || ''} onChange={(e) => set('contact_email', e.target.value)} className={inp} /></div>
        <div className="pt-2 border-t border-[#F1F2F4]"><p className="text-xs font-bold text-[#8D8880] uppercase tracking-wide mb-2">Group discount code</p></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Lbl>Discount code</Lbl><input value={f.discount_code} onChange={(e) => set('discount_code', e.target.value.toUpperCase())} className={`${inp} font-mono uppercase`} data-testid="group-form-code" /></div>
          <div><Lbl>Value</Lbl><input type="number" value={f.discount_value} onChange={(e) => set('discount_value', e.target.value)} className={inp} /></div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div><Lbl>Type</Lbl><select value={f.discount_type} onChange={(e) => set('discount_type', e.target.value)} className={inp}><option value="percentage">%</option><option value="fixed_amount">$</option></select></div>
          <div><Lbl>Plan</Lbl><select value={f.applies_to_plan} onChange={(e) => set('applies_to_plan', e.target.value)} className={inp}><option value="all">All</option><option value="premium">Premium</option><option value="family">Family</option></select></div>
          <div><Lbl>Cycle</Lbl><select value={f.applies_to_billing_cycle} onChange={(e) => set('applies_to_billing_cycle', e.target.value)} className={inp}><option value="all">All</option><option value="monthly">Monthly</option><option value="yearly">Yearly</option></select></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Lbl>Start date</Lbl><input type="date" value={f.start_date || ''} onChange={(e) => set('start_date', e.target.value)} className={inp} /></div>
          <div><Lbl>Expiration date</Lbl><input type="date" value={f.expiration_date || ''} onChange={(e) => set('expiration_date', e.target.value)} className={inp} /></div>
        </div>
        <div><Lbl>Notes</Lbl><textarea value={f.notes || ''} onChange={(e) => set('notes', e.target.value)} rows={2} className="w-full px-3 py-2 rounded-xl border border-[#E8EAEE] text-sm outline-none resize-none" /></div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="h-10 px-5 rounded-xl border font-medium text-sm" style={{ borderColor: C.line, color: C.ink }}>Cancel</button>
          <button onClick={save} disabled={saving} className="h-10 px-6 rounded-xl text-white font-medium text-sm flex items-center gap-2 disabled:opacity-60" style={{ background: C.navy }} data-testid="group-form-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} {isEdit ? 'Save' : 'Create group'}</button>
        </div>
      </div>
    </Modal>
  );
}

function GroupUsersModal({ g, onClose }) {
  const { data, loading } = useData(`/groups/${g.group_id}/users`);
  return (
    <Modal title={`Members — ${g.company_name}`} onClose={onClose}>
      {loading ? <Loading /> : !data?.users?.length ? <Empty text="No members registered yet." /> : (
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead><tr className="text-left text-[#8D8880] border-b border-[#E8EAEE]">{['Name', 'Email', 'Plan', 'Code'].map((h) => <th key={h} className="px-2 py-2 font-medium">{h}</th>)}</tr></thead>
          <tbody>{data.users.map((u) => <tr key={u.user_id} className="border-b border-[#F1F2F4]"><td className="px-2 py-2">{u.name}</td><td className="px-2 py-2">{u.email}</td><td className="px-2 py-2 capitalize">{u.plan}</td><td className="px-2 py-2 font-mono">{u.discount_code_used}</td></tr>)}</tbody>
        </table></div>
      )}
    </Modal>
  );
}

// ---------- Discount Reports ----------
function ReportsModal({ onClose }) {
  const { data, loading } = useData('/discounts/reports');
  const exportCsv = () => {
    if (!data) return;
    const rows = [['Code', 'Name', 'Users', 'Revenue before', 'Revenue after']].concat(data.per_code.map((c) => [c.code, c.name, c.users, c.revenue_before, c.revenue_after]));
    const csv = rows.map((r) => r.map((x) => `"${x ?? ''}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a'); a.href = url; a.download = 'discount-report.csv'; a.click(); URL.revokeObjectURL(url);
  };
  const [pdfLoading, setPdfLoading] = useState(false);
  const exportPdf = async () => {
    setPdfLoading(true);
    try {
      const r = await adminAxios().get('/discounts/reports/pdf', { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = 'discount-report.pdf'; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); } finally { setPdfLoading(false); }
  };
  return (
    <Modal title="Discount reports" onClose={onClose}>
      {loading ? <Loading /> : !data ? <Empty text="No data." /> : (
        <div className="space-y-4" data-testid="discount-reports">
          <div className="grid grid-cols-3 gap-3">
            <Stat label="Revenue before" value={fmtMoney(data.revenue_before)} />
            <Stat label="Revenue after" value={fmtMoney(data.revenue_after)} accent="#7FA98C" />
            <Stat label="Total discounted" value={fmtMoney(data.total_discounted)} accent="#C9A24B" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-2"><p className="font-semibold text-sm" style={{ color: C.ink }}>By code</p><div className="flex gap-2"><button onClick={exportCsv} className="text-xs px-3 py-1.5 rounded-full border flex items-center gap-1.5" style={{ borderColor: C.line, color: C.navy }} data-testid="reports-export-csv"><Download className="h-3.5 w-3.5" /> CSV</button><button onClick={exportPdf} disabled={pdfLoading} className="text-xs px-3 py-1.5 rounded-full border flex items-center gap-1.5 disabled:opacity-60" style={{ borderColor: C.line, color: C.navy }} data-testid="reports-export-pdf">{pdfLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} PDF</button></div></div>
            <div className="overflow-x-auto max-h-64 overflow-y-auto"><table className="w-full text-sm">
              <thead><tr className="text-left text-[#8D8880] border-b border-[#E8EAEE]">{['Code', 'Users', 'Before', 'After'].map((h) => <th key={h} className="px-2 py-2 font-medium">{h}</th>)}</tr></thead>
              <tbody>{data.per_code.map((c) => <tr key={c.code} className="border-b border-[#F1F2F4]"><td className="px-2 py-2 font-mono font-bold" style={{ color: C.navy }}>{c.code}</td><td className="px-2 py-2">{c.users}</td><td className="px-2 py-2">{fmtMoney(c.revenue_before)}</td><td className="px-2 py-2">{fmtMoney(c.revenue_after)}</td></tr>)}</tbody>
            </table></div>
          </div>
          {data.per_group?.length > 0 && (
            <div>
              <p className="font-semibold text-sm mb-2" style={{ color: C.ink }}>By group/company</p>
              <div className="overflow-x-auto"><table className="w-full text-sm">
                <thead><tr className="text-left text-[#8D8880] border-b border-[#E8EAEE]">{['Company', 'Code', 'Members'].map((h) => <th key={h} className="px-2 py-2 font-medium">{h}</th>)}</tr></thead>
                <tbody>{data.per_group.map((g) => <tr key={g.company_name} className="border-b border-[#F1F2F4]"><td className="px-2 py-2">{g.company_name}</td><td className="px-2 py-2 font-mono">{g.code}</td><td className="px-2 py-2">{g.users}{g.eligible ? ` / ${g.eligible}` : ''}</td></tr>)}</tbody>
              </table></div>
            </div>
          )}
          {data.expiring_soon?.length > 0 && (
            <div><p className="font-semibold text-sm mb-2" style={{ color: C.ink }}>Expiring soon</p>
              {data.expiring_soon.map((e) => <p key={e.code} className="text-sm" style={{ color: C.mute }}><span className="font-mono font-bold" style={{ color: C.navy }}>{e.code}</span> — {e.days_left} day(s) left</p>)}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

// ---------- Marketing Materials ----------
const MATERIAL_CATS = [['individuals', 'Individuals & Families'], ['companies', 'Companies & Partners']];

function Materials({ me }) {
  const canEdit = me?.caps?.includes('*');
  const { data, loading, reload } = useData('/materials');
  const [cat, setCat] = useState('individuals');
  const [title, setTitle] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const upload = async () => {
    if (!title.trim() || !file) { toast.error('Add a title and choose an image'); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append('category', cat); fd.append('title', title.trim()); fd.append('image', file);
    try {
      await adminAxios().post('/materials', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Material added'); setTitle(''); setFile(null); reload();
    } catch (e) { toast.error(apiError(e)); } finally { setUploading(false); }
  };
  const del = async (m) => { if (!window.confirm(`Delete "${m.title}"?`)) return; try { await adminAxios().delete(`/materials/${m.material_id}`); toast.success('Deleted'); reload(); } catch (e) { toast.error(apiError(e)); } };

  const items = (data?.materials || []).filter((m) => m.category === cat);
  return (
    <div className="space-y-6" data-testid="admin-materials">
      <div className="flex flex-wrap items-center gap-2">
        {MATERIAL_CATS.map(([v, l]) => (
          <button key={v} onClick={() => setCat(v)} className={`px-4 h-9 rounded-full text-sm font-medium ${cat === v ? 'text-white' : ''}`} style={cat === v ? { background: C.navy } : { color: C.ink, border: `1px solid ${C.line}` }} data-testid={`materials-tab-${v}`}>{l}</button>
        ))}
      </div>

      {canEdit && (
        <Card className="p-5">
          <p className="font-semibold mb-3" style={{ color: C.ink }}>Add a page to “{MATERIAL_CATS.find((x) => x[0] === cat)[1]}”</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Page title (e.g., What You Can Store)" className="flex-1 h-10 px-3 rounded-xl border border-[#E8EAEE] text-sm outline-none" data-testid="material-title" />
            <input type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => setFile(e.target.files[0])} className="text-sm" data-testid="material-file" />
            <button onClick={upload} disabled={uploading} className="h-10 px-5 rounded-xl text-white text-sm font-medium flex items-center gap-2 disabled:opacity-60" style={{ background: C.navy }} data-testid="material-upload">{uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add page</button>
          </div>
          <p className="text-xs mt-2" style={{ color: C.mute }}>JPG, PNG, or WebP · max 5MB. Pages appear in the Ambassador Resources page in the order added.</p>
        </Card>
      )}

      {loading ? <Loading /> : !items.length ? <Empty text="No pages in this section yet." /> : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {items.map((m) => (
            <div key={m.material_id} className="group relative" data-testid={`admin-material-${m.material_id}`}>
              <div className="aspect-[3/4] rounded-xl overflow-hidden border border-[#E8EAEE] bg-[#F7F8FA]"><img src={m.image_url} alt={m.title} loading="lazy" className="w-full h-full object-cover" /></div>
              <p className="text-xs mt-1.5 truncate" style={{ color: C.mute }}>{m.title}</p>
              {canEdit && <button onClick={() => del(m)} className="absolute top-2 right-2 p-1.5 rounded-lg bg-white/90 shadow" style={{ color: '#C0563F' }} data-testid={`material-delete-${m.material_id}`}><Trash2 className="h-4 w-4" /></button>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
